import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from 'uuid'; // Importando uuid para IDs únicos
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'; // Importando DragDropContext, Droppable e Draggable

import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "../App";
import { toast } from "sonner";
import IconeEscola from '../assets/iconeescola.png';
import Chart from 'chart.js/auto';
import TrailsManager from "./TrailsManager";
import VideoUploadManager from "./VideoUploadManager";
import {
  Users,
  BookOpen,
  BarChart3,
  Plus,
  Settings,
  Home,
  LogOut,
  Edit,
  Trash2,
  Eye,
  Award,
  TrendingUp,
  CheckCircle2,
  FileText,
  Brain,
  Globe,
  Minus,
  Save,
  Loader2,
  Target,
  Route,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  ClipboardList,
  Mail,
  Phone,
  MapPin,
  Shield,
  Briefcase,
  Video,
  User,
  GraduationCap,
  Building,
  Calendar,
  Upload,
  Image as ImageIcon,
  Link2,
  Clock,
  GripVertical,
  Layers,
  Lock,
  Search,
  Bell,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
} from "lucide-react";// Componente Wrapper para resolver o bug do Strict Mode do React com react-beautiful-dnd
// Colocado aqui para evitar problemas de importação em outros arquivos.
const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to extract error messages from API responses
const getErrorMessage = (error, defaultMessage = "Ocorreu um erro") => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    // Handle Pydantic validation errors (array of error objects)
    if (Array.isArray(detail)) {
      return detail[0]?.msg || defaultMessage;
    }
    // Handle string error messages
    if (typeof detail === 'string') {
      return detail;
    }
  }
  return defaultMessage;
};

// Função auxiliar para reordenar arrays
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result.map((item, index) => ({ ...item, order: index + 1 })); // Atualiza a propriedade 'order'
};


const AdminDashboard = () => {
  const { user, logout, token } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [settings, setSettings] = useState({});
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progressUsers, setProgressUsers] = useState([]);
  const [reportCourseId, setReportCourseId] = useState("");
  const [peopleFilter, setPeopleFilter] = useState('all'); // all | approved | failed
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [systemUserMode, setSystemUserMode] = useState(false); // true = criando admin/coordenador, false = aluno
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingSector, setEditingSector] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudentLoading, setSelectedStudentLoading] = useState(false);
  const [studentActiveTab, setStudentActiveTab] = useState('courses');

  // Course artifacts job state
  const [courseArtifactsJobData, setCourseArtifactsJobData] = useState(null);
  const [courseArtifactsJobModalOpen, setCourseArtifactsJobModalOpen] = useState(false);
  const [courseArtifactsJobId, setCourseArtifactsJobId] = useState(null);
  const [courseArtifactsJobLoading, setCourseArtifactsJobLoading] = useState(false);
  const [courseArtifactsJobAction, setCourseArtifactsJobAction] = useState(null);
  const courseArtifactsJobPollRef = useRef(null);
  const [segmentTranscriptionStatus, setSegmentTranscriptionStatus] = useState({});

  // Admin sidebar state
  const [adminSidebarCollapsed, setAdminSidebarCollapsed] = useState(false);
  // Last visited cadastros sub-tab (users | courses | assessments)
  const [cadastrosTab, setCadastrosTab] = useState('users');
  const CADASTROS_TABS = ['users', 'courses', 'assessments', 'sectors'];
  const inCadastros = CADASTROS_TABS.includes(activeTab);

  // Confirm delete dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const openConfirmDialog = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };
  const closeConfirmDialog = () => setConfirmDialog(d => ({ ...d, open: false, onConfirm: null }));

  // Course form state
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    external_url: "",
    thumbnail_url: "",
    sectors: [],
    segments: [],
    trail_order: 0,
    requires_assessment: false,
    linked_assessment_id: null,
    passing_score_required: 70.0
  });
  const [courseAssessmentAIConfig, setCourseAssessmentAIConfig] = useState({
    num_questions: "10"
  });
  const [generatingCourseAssessment, setGeneratingCourseAssessment] = useState(false);

  // Image upload state
  const [imageUploading, setImageUploading] = useState(false);

  // User form state
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    sector: "",
    role: "employee",
    // Campos adicionais de perfil
    phone: "",
    address: "",
    code: "",
    shift: "",
    period: "",
    modality: "",
    start_date: "",
    status: "",
    institution: "",
    education_level: "",
    mentor: ""
  });

  // Sector form state
  const [sectorForm, setSectorForm] = useState({
    name: "",
    display_name: "",
    description: ""
  });

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    welcome_video_url: "",
    welcome_video_title: "",
    welcome_video_description: "",
    show_welcome_video: true
  });

  // Admin own profile form state
  const [adminProfileForm, setAdminProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    code: "",
    shift: "",
    period: "",
    modality: "",
    start_date: "",
    institution: "",
    education_level: "",
  });
  const [adminProfileLoading, setAdminProfileLoading] = useState(false);

  // Assessment form state
  const [assessmentForm, setAssessmentForm] = useState({
    title: "",
    description: "",
    course_id: "",
    time_limit: 60,
    passing_grade: 70.0,
    max_attempts: 3,
    cooldown_period: 24,
    sectors: []
  });

  // Questions state
  const [questions, setQuestions] = useState([
    {
      question_text: "",
      options: ["A) ", "B) ", "C) ", "D) "],
      correct_answer: "A",
      explanation: "",
      points: 1.0
    }
  ]);

  // AI Generation state
  const [aiGenerationData, setAiGenerationData] = useState({
    url: "",
    description: "",
    num_questions: 10,
    assessment_title: ""
  });

  // Text parsing state
  const [textImportData, setTextImportData] = useState({
    text: "",
    assessment_title: ""
  });

  // Assessment creation states
  const [activeAssessmentTab, setActiveAssessmentTab] = useState("manual");
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [hasAiGenerated, setHasAiGenerated] = useState(false);

  // New segment state
  const [newSegment, setNewSegment] = useState({
    title: "",
    description: "",
    video_url: "",
    video_id: null,
    duration_minutes: 0,
    order: 1,
    video_type: "local" // "local" ou "youtube"
  });

  // Video upload manager state
  const [showVideoUploadManager, setShowVideoUploadManager] = useState(false);
  const [createTrailSignal, setCreateTrailSignal] = useState(0);

  const resetCourseForm = () => {
    setEditingCourse(null);
    setCourseForm({
      title: "",
      description: "",
      external_url: "",
      thumbnail_url: "",
      sectors: [],
      segments: [],
      trail_order: 0,
      requires_assessment: false,
      linked_assessment_id: null,
      passing_score_required: 70.0
    });
    setCourseAssessmentAIConfig({ num_questions: "10" });
  };

  useEffect(() => {
    if (token) {
      fetchAdminData();
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      setAdminProfileForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
        code: user.code || "",
        shift: user.shift || "",
        period: user.period || "",
        modality: user.modality || "",
        start_date: user.start_date || "",
        institution: user.institution || "",
        education_level: user.education_level || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (courseDialogOpen && editingCourse?.id) {
      attachToActiveCourseJob(editingCourse.id);
    }
    if (!courseDialogOpen) {
      stopCourseJobPolling();
      setCourseArtifactsJobModalOpen(false);
    }
  }, [courseDialogOpen, editingCourse]);

  // Impede que o Dialog de edição do curso seja fechado enquanto o upload de vídeo estiver aberto
  const handleCourseDialogOpenChange = (open) => {
    if (!open && showVideoUploadManager) {
      // Ignora tentativas de fechar o Dialog (ex: clique externo) enquanto o modal de upload estiver aberto
      return;
    }
    setCourseDialogOpen(open);
  };

  // Adiciona uma classe no <body> enquanto o VideoUploadManager estiver aberto,
  // para permitir que o overlay do Dialog seja desativado via CSS/JS (ver ui/dialog.jsx).
  useEffect(() => {
    const cls = 'child-modal-open';
    if (showVideoUploadManager) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [showVideoUploadManager]);

  // Charts refs and instances
  const sectorChartRef = useRef(null);
  const courseCompletionChartRef = useRef(null);
  const passFailChartRef = useRef(null);
  const sectorChartInstance = useRef(null);
  const completionChartInstance = useRef(null);
  const passFailChartInstance = useRef(null);

  // Handler para quando um item é arrastado e solto
  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(
      courseForm.segments,
      result.source.index,
      result.destination.index
    );

    // Atualiza o estado local imediatamente para a UI ficar fluida
    setCourseForm(prev => ({ ...prev, segments: items }));

    // 👉 Inicia a chamada para o backend para salvar a nova ordem
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // CUIDADO: Se você estiver editando um curso, 'editingCourse' deve ter um ID.
      // Se for um novo curso, ele não terá. Essa lógica precisa ser mais robusta
      // para lidar com ambos os casos.
      if (!editingCourse) {
        toast.info("Salve o curso primeiro para que a ordem dos módulos seja persistida.");
        return; // Não tenta salvar no backend se não houver um curso editando
      }

      // Precisamos montar o objeto completo do curso para enviar para a rota de update
      // É importante pegar o estado MAIS RECENTE do courseForm e sobrescrever apenas os segmentos
      const updatedCourseData = {
        ...courseForm, // Pega os dados atuais do formulário
        segments: items, // Usa a nova lista de segmentos ordenada
        // Garante que o ID do curso está presente para a requisição PUT
        id: editingCourse.id 
      };

      // Removemos 'access_info' se ele existir, pois não deve ser enviado de volta
      if (updatedCourseData.access_info) {
        delete updatedCourseData.access_info;
      }
      
      await axios.put(`${API}/courses/${editingCourse.id}`, updatedCourseData, { headers });
      toast.success("Ordem dos módulos salva!");

    } catch (error) {
      console.error("Erro ao salvar a ordem dos módulos:", error);
      toast.error("Não foi possível salvar a nova ordem.");
      // Opcional: Reverter a mudança no estado se a API falhar
      // setCourseForm(prev => ({ ...prev, segments: originalItems }));
    }
  };

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all admin data
      const [coursesResponse, usersResponse, analyticsResponse, pendingUsersResponse, sectorsResponse, settingsResponse, assessmentsResponse, progressResponse] = await Promise.all([
        axios.get(`${API}/courses`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/pending-users`, { headers }),
        axios.get(`${API}/sectors`, { headers }),
        axios.get(`${API}/settings`, { headers }),
        axios.get(`${API}/assessments/manage`, { headers }),
        axios.get(`${API}/admin/progress`, { headers })
      ]);
      
      setCourses(coursesResponse.data);
      setUsers(usersResponse.data);
      setAnalytics(analyticsResponse.data);
      setPendingUsers(pendingUsersResponse.data);
      setSectors(sectorsResponse.data);
      setSettings(settingsResponse.data);
      setAssessments(assessmentsResponse.data);
      setProgressUsers(progressResponse.data?.users || []);
      if (!reportCourseId && coursesResponse.data?.length) {
        setReportCourseId(coursesResponse.data[0].id);
      }
      
      // Update settings form with current values
      setSettingsForm({
        welcome_video_url: settingsResponse.data.welcome_video_url || "",
        welcome_video_title: settingsResponse.data.welcome_video_title || "",
        welcome_video_description: settingsResponse.data.welcome_video_description || "",
        show_welcome_video: settingsResponse.data.show_welcome_video || true
      });
      
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Erro ao carregar dados administrativos");
    } finally {
      setLoading(false);
    }
  };

  const loadStudent = async (studentId) => {
    if (!studentId) return;
    setSelectedStudentLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/admin/reports/student/${studentId}`, { headers });
      setSelectedStudent(res.data);
      // keep the current tab (Visão geral) and just swap its contents
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error loading student:', err);
      toast.error('Erro ao carregar dados do aluno');
    } finally {
      setSelectedStudentLoading(false);
    }
  };

  const closeStudentView = () => {
    setSelectedStudent(null);
  };

  const stopCourseJobPolling = () => {
    if (courseArtifactsJobPollRef.current) {
      clearInterval(courseArtifactsJobPollRef.current);
      courseArtifactsJobPollRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopCourseJobPolling();
    };
  }, []);

  const renderStudentProfile = () => {
    if (!selectedStudent) return null;
    if (selectedStudentLoading) {
      return (
        <div className="min-h-[200px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      );
    }

    const completedCourses = selectedStudent.courses?.completed
      ?? (selectedStudent.course_progress || []).filter(c => c.is_completed);
    const activeCourses = selectedStudent.courses?.active
      ?? (selectedStudent.course_progress || []).filter(c => !c.is_completed);
    const certs = selectedStudent.certificates || [];
    const timeline = selectedStudent.timeline || [];

    const kpiCard = (label, value, color) => (
      <div className={`rounded-xl border p-3 flex flex-col gap-0.5 bg-gray-900/60 ${color}`}>
        <p className="text-[0.65rem] uppercase tracking-widest text-gray-400">{label}</p>
        <p className="text-xl font-bold text-white">{value}</p>
      </div>
    );

    const infoRow = (label, value) => (
      <div className="flex items-start gap-2 py-1.5 border-b border-gray-800 last:border-0">
        <span className="text-xs text-gray-500 min-w-[130px] flex-shrink-0">{label}</span>
        <span className="text-xs text-gray-200 break-words">{value || '—'}</span>
      </div>
    );

    const timelineColors = {
      enroll: { dot: 'bg-indigo-500', badge: 'bg-indigo-900/40 text-indigo-300 border-indigo-600', label: 'Matrícula' },
      course: { dot: 'bg-sky-500',    badge: 'bg-sky-900/40 text-sky-300 border-sky-600',       label: 'Curso' },
      exam:   { dot: 'bg-amber-500',  badge: 'bg-amber-900/40 text-amber-300 border-amber-600', label: 'Avaliação' },
      cert:   { dot: 'bg-emerald-500',badge: 'bg-emerald-900/40 text-emerald-300 border-emerald-600', label: 'Certificado' },
    };

    return (
      <div className="space-y-6 pb-12">
        {/* ── Cabeçalho ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={closeStudentView}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Voltar
            </button>
            <span className="text-gray-700">|</span>
            <h2 className="text-lg font-semibold text-white">Perfil do Aluno</h2>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${
            selectedStudent.is_active !== false
              ? 'bg-emerald-900/40 text-emerald-300 border-emerald-600'
              : 'bg-red-900/40 text-red-300 border-red-700'
          }`}>
            {selectedStudent.is_active !== false ? 'Ativo' : 'Inativo'}
          </span>
        </div>

        {/* ── Card principal do aluno ── */}
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 flex flex-col sm:flex-row items-start gap-5">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <img
              src={
                selectedStudent.avatarUrl || selectedStudent.avatar_url ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedStudent.name || selectedStudent.email || 'User')}&background=1e3a5f&color=93c5fd&size=128`
              }
              alt="Avatar"
              className="w-20 h-20 rounded-2xl object-cover border-2 border-blue-800 shadow-lg"
            />
          </div>

          {/* Info principal */}
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-xl font-bold text-white leading-tight">
                  {selectedStudent.name || 'Sem nome'}
                </h3>
                <p className="text-sm text-gray-400 break-all">{selectedStudent.email}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs px-2.5 py-1 rounded-full border bg-indigo-900/40 text-indigo-300 border-indigo-600">
                  {selectedStudent.status_label || selectedStudent.role || 'Aluno'}
                </span>
                {selectedStudent.sector && (
                  <span className="text-xs px-2.5 py-1 rounded-full border bg-gray-800 text-gray-300 border-gray-600">
                    {selectedStudent.sector === 'estagio' ? 'Estágio'
                      : selectedStudent.sector === 'tecnico' ? 'Técnico'
                      : selectedStudent.sector === 'comercial' ? 'Comercial'
                      : selectedStudent.sector}
                  </span>
                )}
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {kpiCard('Concluídos', completedCourses.length, 'border-emerald-700/50')}
              {kpiCard('Em andamento', activeCourses.length, 'border-sky-700/50')}
              {kpiCard('Certificados', certs.length, 'border-yellow-700/50')}
              {kpiCard('Média geral', selectedStudent.averageScore ?? selectedStudent.summary?.average ?? '—', 'border-purple-700/50')}
            </div>

            {/* Resumo de desempenho */}
            {studentSummary && (
              <p className="text-xs text-gray-400 italic">{studentSummary}</p>
            )}
          </div>
        </div>

        {/* ── Grid: Trilhas + Dados pessoais ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Trilhas */}
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Trilhas & Turma</p>
            {selectedStudent.trails && selectedStudent.trails.length > 0 ? (
              <div className="space-y-2">
                {selectedStudent.trails.map((trail, i) => (
                  <div key={trail.trail_id || i} className="flex items-center justify-between gap-3 bg-gray-800/60 rounded-xl px-3 py-2 border border-gray-700">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${trail.is_completed ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                      <span className="text-sm text-gray-200 truncate">{trail.trail_name}</span>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {trail.completed_courses}/{trail.total_courses} cursos
                      {trail.is_completed && <span className="ml-1 text-emerald-400">✓</span>}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{selectedStudent.track || 'Sem trilha atribuída'}</p>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {selectedStudent.shift && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-300">
                  Turno: {selectedStudent.shift}
                </span>
              )}
              {selectedStudent.period && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-300">
                  Período: {selectedStudent.period}
                </span>
              )}
              {selectedStudent.modality && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-300">
                  {selectedStudent.modality}
                </span>
              )}
            </div>
          </div>

          {/* Dados pessoais */}
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Dados do Aluno</p>
            {infoRow('Telefone', selectedStudent.phone)}
            {infoRow('Endereço', selectedStudent.address)}
            {infoRow('Matrícula', selectedStudent.code)}
            {infoRow('Data de ingresso', selectedStudent.start_date || selectedStudent.startDate)}
            {infoRow('Instituição', selectedStudent.institution)}
            {infoRow('Nível de formação', selectedStudent.education_level || selectedStudent.educationLevel)}
            {infoRow('Responsável', selectedStudent.mentor)}
            {infoRow('Último acesso', selectedStudent.last_access_at
              ? new Date(selectedStudent.last_access_at).toLocaleString('pt-BR')
              : selectedStudent.lastAccess)}
          </div>
        </div>

        {/* ── Abas: Cursos / Certificados / Linha do tempo ── */}
        <div className="bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden">
          {/* Tab header */}
          <div className="flex border-b border-gray-700 px-1 pt-1 gap-1">
            {[
              { key: 'courses',  label: 'Cursos & Progresso' },
              { key: 'certs',    label: `Certificados (${certs.length})` },
              { key: 'timeline', label: 'Linha do tempo' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setStudentActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                  studentActiveTab === tab.key
                    ? 'bg-gray-800 text-white border-b-2 border-blue-500'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-5">
            {/* ── Tab: Cursos ── */}
            {studentActiveTab === 'courses' && (
              <div className="space-y-6">
                {/* Concluídos */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                      Cursos concluídos
                    </p>
                    <span className="text-xs text-gray-500">{completedCourses.length} curso(s)</span>
                  </div>
                  {completedCourses.length === 0 ? (
                    <p className="text-sm text-gray-600 py-2">Nenhum curso concluído ainda.</p>
                  ) : (
                    <div className="space-y-2">
                      {completedCourses.map(course => {
                        const endDate = course.completed_at
                          ? new Date(course.completed_at).toLocaleDateString('pt-BR')
                          : (course.end || course.completedAt || '—');
                        const finalScore = course.final_score != null
                          ? `${Math.round(course.final_score)}%`
                          : (course.finalScore ?? course.score ?? '—');
                        return (
                          <div key={course.id || course.course_id}
                            className="flex items-center justify-between gap-3 bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">
                                  {course.name || course.course_title}
                                </p>
                                <p className="text-xs text-gray-500">Concluído em {endDate}</p>
                              </div>
                            </div>
                            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-700 flex-shrink-0">
                              {finalScore}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Em andamento */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                      Em andamento
                    </p>
                    <span className="text-xs text-gray-500">{activeCourses.length} curso(s)</span>
                  </div>
                  {activeCourses.length === 0 ? (
                    <p className="text-sm text-gray-600 py-2">Nenhum curso em andamento.</p>
                  ) : (
                    <div className="space-y-2">
                      {activeCourses.map(course => {
                        const pct = course.progressPercent ?? course.completion_percentage ?? 0;
                        const startDate = course.started_at
                          ? new Date(course.started_at).toLocaleDateString('pt-BR')
                          : (course.startedAt || course.start || '—');
                        return (
                          <div key={course.id || course.course_id}
                            className="bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-medium text-white truncate">
                                {course.name || course.course_title}
                              </p>
                              <span className="text-xs text-gray-400 flex-shrink-0">{pct}%</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="h-1.5 rounded-full bg-blue-500 transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500">Iniciado em {startDate}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Tab: Certificados ── */}
            {studentActiveTab === 'certs' && (
              <div className="space-y-3">
                {certs.length === 0 ? (
                  <div className="text-center py-10">
                    <Award className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Nenhum certificado emitido ainda.</p>
                  </div>
                ) : (
                  certs.map(cert => {
                    const courseProgress = (selectedStudent.course_progress || [])
                      .find(p => p.course_id === (cert.course_id || cert.courseId));
                    const displayScore = cert.score ?? courseProgress?.final_score ?? null;
                    const issuedAt = cert.issued_at
                      ? new Date(cert.issued_at).toLocaleDateString('pt-BR')
                      : (cert.issuedAt || '—');
                    const certTitle = cert.course_name || cert.name || cert.course_title || 'Certificado';
                    const downloadUrl = cert.url || `/api/certificates/${cert.id}/download`;
                    return (
                      <div key={cert.id}
                        className="flex items-center justify-between gap-4 bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                            <Award className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">{certTitle}</p>
                            <p className="text-xs text-gray-500">
                              Emitido em {issuedAt}
                              {displayScore != null && ` • Nota: ${Math.round(displayScore)}%`}
                            </p>
                          </div>
                        </div>
                        <a
                          href={downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 rounded-lg border border-sky-700 text-sky-300 bg-sky-900/30 hover:bg-sky-900/60 transition-colors flex-shrink-0"
                        >
                          Ver
                        </a>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ── Tab: Linha do tempo ── */}
            {studentActiveTab === 'timeline' && (
              <div>
                {timeline.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-sm text-gray-500">Nenhum evento registrado.</p>
                  </div>
                ) : (
                  <div className="relative space-y-0 pl-5">
                    {/* Linha vertical */}
                    <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gray-700" />
                    {timeline.map((item, idx) => {
                      const tc = timelineColors[item.type] || {
                        dot: 'bg-gray-500',
                        badge: 'bg-gray-800 text-gray-300 border-gray-600',
                        label: 'Evento',
                      };
                      return (
                        <div key={idx} className="relative flex gap-4 pb-5 last:pb-0">
                          <div className={`absolute -left-[11px] top-1 w-3 h-3 rounded-full border-2 border-gray-900 ${tc.dot} flex-shrink-0`} />
                          <div className="flex-1 flex items-start justify-between gap-3 bg-gray-800/40 border border-gray-700/60 rounded-xl px-4 py-3">
                            <div>
                              <p className="text-sm font-medium text-white">{item.label}</p>
                              {item.detail && <p className="text-xs text-gray-400 mt-0.5">{item.detail}</p>}
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${tc.badge}`}>
                                {tc.label}
                              </span>
                              {item.date && <span className="text-xs text-gray-500">{item.date}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Texto descritivo do resumo do aluno — replica a lógica do mock HTML
  const studentSummary = React.useMemo(() => {
    const avg = selectedStudent?.averageScore ?? selectedStudent?.summary?.average;
    if (avg === undefined || avg === null) return 'Carregando resumo...';
    if (Number(avg) >= 85) return 'Desempenho excelente, acima da média da turma.';
    if (Number(avg) >= 70) return 'Bom desempenho, com espaço para evoluir em alguns módulos.';
    return 'Atenção: desempenho abaixo do ideal. Reforçar trilhas de base.';
  }, [selectedStudent]);

  const artifactLabelMap = {
    mindMap: 'Mapa mental',
    flashcards: 'Flashcards',
    quiz: 'Quiz',
    slides: 'Slides',
    infographic: 'Infográfico'
  };

  const fetchCourseArtifactsJobStatus = async (jobId, courseId) => {
    if (!jobId || !courseId || !token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/courses/${courseId}/artifacts/jobs/${jobId}`, { headers });
      setCourseArtifactsJobData(response.data);
      const status = response.data?.job?.status;
      if (status && status !== 'PENDING' && status !== 'RUNNING') {
        stopCourseJobPolling();
        setCourseArtifactsJobId(null);
      }
    } catch (error) {
      console.error('Erro ao obter status do job de artefatos:', error);
      stopCourseJobPolling();
      toast.error('Erro ao acompanhar o job de artefatos');
    }
  };

  const startCourseArtifactsJobPolling = (jobId, courseId) => {
    if (!jobId || !courseId) return;
    setCourseArtifactsJobId(jobId);
    fetchCourseArtifactsJobStatus(jobId, courseId);
    stopCourseJobPolling();
    courseArtifactsJobPollRef.current = setInterval(() => {
      fetchCourseArtifactsJobStatus(jobId, courseId);
    }, 4000);
    setCourseArtifactsJobModalOpen(true);
  };

  const attachToActiveCourseJob = async (courseId) => {
    if (!courseId || !token || courseArtifactsJobId) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/courses/${courseId}/artifacts/jobs/active`, { headers });
      if (response.data?.job?.id) {
        setCourseArtifactsJobData(response.data);
        startCourseArtifactsJobPolling(response.data.job.id, courseId);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Erro ao verificar job de artefatos ativo:', error);
      }
    }
  };

  const handleCourseArtifactsJobStart = async (mode = 'generate') => {
    if (!editingCourse?.id) {
      toast.error('Salve o curso antes de gerar artefatos.');
      return;
    }
    try {
      setCourseArtifactsJobLoading(true);
      setCourseArtifactsJobAction(mode);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/courses/${editingCourse.id}/artifacts/generate`, { mode }, { headers });
      const jobId = response.data?.job_id;
      if (jobId) {
        startCourseArtifactsJobPolling(jobId, editingCourse.id);
        toast.success(mode === 'regenerate' ? 'Regeração iniciada!' : 'Geração iniciada!');
      }
    } catch (error) {
      console.error('Erro ao iniciar job de artefatos:', error);
      const detail = error.response?.data?.detail || 'Não foi possível iniciar a geração dos artefatos';
      toast.error(detail);
    } finally {
      setCourseArtifactsJobLoading(false);
      setCourseArtifactsJobAction(null);
    }
  };

  const handleCourseJobModalChange = (open) => {
    setCourseArtifactsJobModalOpen(open);
    if (!open) {
      stopCourseJobPolling();
    } else if (open && courseArtifactsJobId && editingCourse?.id) {
      startCourseArtifactsJobPolling(courseArtifactsJobId, editingCourse.id);
    }
  };

  const courseJobInfo = courseArtifactsJobData?.job;
  const currentSegmentStatus = courseArtifactsJobData?.current_segment_status;
  const currentSegmentProgress = courseArtifactsJobData?.current_segment_progress;
  const courseJobStatus = courseJobInfo?.status;
  const isCourseJobActive = courseJobStatus === 'PENDING' || courseJobStatus === 'RUNNING';
  const getCourseJobBadgeClass = () => {
    if (courseJobStatus === 'COMPLETED') return 'bg-green-100 text-green-800';
    if (courseJobStatus === 'ERROR') return 'bg-red-100 text-red-700';
    if (isCourseJobActive) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-600';
  };

  const getTranscriptionBadgeConfig = (status) => {
    switch (status) {
      case 'checking':
        return { label: 'Verificando...', className: 'border-slate-300 text-slate-600 bg-slate-50' };
      case 'completed':
        return { label: 'Transcrição pronta', className: 'border-emerald-500/60 text-emerald-700 bg-emerald-50' };
      case 'processing':
        return { label: 'Transcrição em andamento', className: 'border-sky-500/60 text-sky-700 bg-sky-50' };
      case 'pending':
        return { label: 'Transcrição pendente', className: 'border-amber-500/60 text-amber-700 bg-amber-50' };
      case 'failed':
        return { label: 'Falha na transcrição', className: 'border-rose-500/60 text-rose-700 bg-rose-50' };
      default:
        return { label: 'Sem transcrição', className: 'border-gray-300 text-gray-600 bg-gray-50' };
    }
  };

  useEffect(() => {
    if (!courseDialogOpen || !editingCourse?.id || !courseForm.segments.length || !token) {
      setSegmentTranscriptionStatus({});
      return;
    }

    let isActive = true;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchSegmentStatuses = async () => {
      const results = await Promise.all(
        courseForm.segments.map(async (segment) => {
          if (!segment?.id) return null;
          if (segment.video_id) {
            try {
              const { data } = await axios.get(`${API}/videos/${segment.video_id}/status`, { headers });
              return { id: segment.id, status: data?.status || 'pending' };
            } catch (error) {
              return { id: segment.id, status: 'missing' };
            }
          }
          try {
            const { data } = await axios.get(`${API}/video-artifacts/${segment.id}`, { headers });
            if (data?.status === 'COMPLETED') return { id: segment.id, status: 'completed' };
            if (data?.status === 'ERROR') return { id: segment.id, status: 'failed' };
            return { id: segment.id, status: 'processing' };
          } catch (error) {
            return { id: segment.id, status: 'missing' };
          }
        })
      );

      if (!isActive) return;
      const nextStatus = {};
      results.forEach((result) => {
        if (result?.id) nextStatus[result.id] = result.status;
      });
      setSegmentTranscriptionStatus(nextStatus);
    };

    fetchSegmentStatuses();

    return () => {
      isActive = false;
    };
  }, [courseDialogOpen, editingCourse?.id, courseForm.segments, token]);

  const getSegmentStageLabel = () => {
    switch (currentSegmentStatus) {
      case 'DOWNLOADING':
        return 'Baixando áudio do YouTube';
      case 'TRANSCRIBING':
        return 'Transcrevendo vídeo';
      case 'PROCESSED':
      case 'COMPLETED':
        return 'Transcrição concluída';
      case 'GENERATING':
        return 'Gerando artefato com IA';
      case 'ERROR':
        return 'Erro ao processar este vídeo';
      default:
        if (courseJobInfo?.stage === 'GENERATING_AI') return 'Gerando artefato com IA';
        if (courseJobInfo?.stage === 'TRANSCRIPTION') return 'Preparando transcrição';
        return 'Em fila para processamento';
    }
  };

  // Compute helpers for Reports
  const sectorCounts = React.useMemo(() => {
    const counts = {};
    for (const u of users) {
      const s = u.sector || '—';
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [users]);

  const completionByCourse = React.useMemo(() => {
    // percentage of employees who completed each course
    const employees = users.filter(u => u.role === 'employee');
    const totalPeople = employees.length || 1;
    const byCourse = {};
    for (const c of courses) {
      const id = c.id;
      const done = progressUsers.filter(p => p.progress && p.progress.find(x => x.course_id === id && x.is_completed)).length;
      byCourse[id] = Math.round((done / totalPeople) * 100);
    }
    return byCourse;
  }, [users, courses, progressUsers]);

  const filteredPeople = React.useMemo(() => {
    const employees = users.filter(u => u.role === 'employee');
    return employees.filter(u => {
      const pu = progressUsers.find(p => p.user_id === u.id) || { summary: { total_courses: 0, completed: 0 } };
      const total = pu.summary?.total_courses || 0;
      const done = pu.summary?.completed || 0;
      const approved = total > 0 && done >= total;
      if (peopleFilter === 'approved') return approved;
      if (peopleFilter === 'failed') return !approved;
      return true;
    });
  }, [users, progressUsers, peopleFilter]);

  // Initialize/Update charts when data changes
  useEffect(() => {
    // Only render overview charts when the overview tab is active and no student profile is open
    if (activeTab !== 'overview' || selectedStudent) return;
    // Wait a tick to ensure canvases are mounted
    const t = setTimeout(() => {
      // Destroy previous instances
      if (sectorChartInstance.current) { sectorChartInstance.current.destroy(); sectorChartInstance.current = null; }
      if (completionChartInstance.current) { completionChartInstance.current.destroy(); completionChartInstance.current = null; }
      if (passFailChartInstance.current) { passFailChartInstance.current.destroy(); passFailChartInstance.current = null; }

      // ── Shared tooltip style ──────────────────────────────────────────
      const ttStyle = {
        backgroundColor: '#16161e',
        titleColor: '#c8c8d8',
        bodyColor: '#6a6a8a',
        borderColor: 'rgba(255,255,255,0.07)',
        borderWidth: 1,
        padding: { x: 12, y: 8 },
        cornerRadius: 8,
        displayColors: false,
      };

      // Muted, desaturated color palette (8 hues)
      const PALETTE = [
        'rgba(99,102,241,0.72)',   // indigo
        'rgba(249,115,22,0.70)',   // orange
        'rgba(34,197,94,0.68)',    // green
        'rgba(168,85,247,0.68)',   // violet
        'rgba(56,189,248,0.68)',   // sky
        'rgba(244,63,94,0.66)',    // rose
        'rgba(20,184,166,0.68)',   // teal
        'rgba(245,158,11,0.66)',   // amber
      ];
      const PALETTE_HOVER = [
        'rgba(99,102,241,0.92)',
        'rgba(249,115,22,0.90)',
        'rgba(34,197,94,0.88)',
        'rgba(168,85,247,0.88)',
        'rgba(56,189,248,0.88)',
        'rgba(244,63,94,0.86)',
        'rgba(20,184,166,0.88)',
        'rgba(245,158,11,0.86)',
      ];

      // ── Chart 1: Sector Distribution — Horizontal bar ────────────────
      if (sectorChartRef.current) {
        const ctx = sectorChartRef.current.getContext('2d');
        const labels = Object.keys(sectorCounts);
        const data = Object.values(sectorCounts);
        sectorChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              data,
              backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]),
              hoverBackgroundColor: labels.map((_, i) => PALETTE_HOVER[i % PALETTE_HOVER.length]),
              borderRadius: 4,
              borderSkipped: false,
            }]
          },
          options: {
            indexAxis: 'y',
            plugins: {
              legend: { display: false },
              tooltip: {
                ...ttStyle,
                callbacks: { label: ctx => `  ${ctx.parsed.x} colaboradores` }
              }
            },
            scales: {
              x: {
                ticks: { color: '#3a3a5a', font: { size: 10, family: 'monospace' }, maxTicksLimit: 5 },
                grid: { color: 'rgba(255,255,255,0.04)' },
                border: { display: false }
              },
              y: {
                ticks: { color: '#7a7a9a', font: { size: 11, family: "'Inter', sans-serif" } },
                grid: { display: false },
                border: { display: false }
              }
            },
            maintainAspectRatio: false,
            responsive: true,
            animation: { duration: 500, easing: 'easeOutQuart' }
          }
        });
      }

      // ── Chart 2: Course Completion — Horizontal bar, color-coded ─────
      if (courseCompletionChartRef.current) {
        const ctx = courseCompletionChartRef.current.getContext('2d');
        const sorted = [...courses]
          .map(c => ({ label: (c.title || c.name || 'Curso'), value: completionByCourse[c.id] || 0 }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8);
        const labels = sorted.map(c => c.label.length > 22 ? c.label.slice(0, 20) + '…' : c.label);
        const data = sorted.map(c => c.value);
        completionChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              data,
              backgroundColor: data.map(v =>
                v >= 70 ? 'rgba(34,197,94,0.65)' :
                v >= 40 ? 'rgba(99,102,241,0.65)' :
                          'rgba(249,115,22,0.62)'
              ),
              hoverBackgroundColor: data.map(v =>
                v >= 70 ? 'rgba(34,197,94,0.88)' :
                v >= 40 ? 'rgba(99,102,241,0.88)' :
                          'rgba(249,115,22,0.85)'
              ),
              borderRadius: 4,
              borderSkipped: false,
            }]
          },
          options: {
            indexAxis: 'y',
            plugins: {
              legend: { display: false },
              tooltip: {
                ...ttStyle,
                callbacks: { label: ctx => `  ${ctx.parsed.x}% concluído` }
              }
            },
            scales: {
              x: {
                min: 0,
                max: 100,
                ticks: {
                  color: '#3a3a5a',
                  font: { size: 10, family: 'monospace' },
                  callback: v => v + '%',
                  maxTicksLimit: 5,
                },
                grid: { color: 'rgba(255,255,255,0.04)' },
                border: { display: false }
              },
              y: {
                ticks: { color: '#7a7a9a', font: { size: 11, family: "'Inter', sans-serif" } },
                grid: { display: false },
                border: { display: false }
              }
            },
            maintainAspectRatio: false,
            responsive: true,
            animation: { duration: 500, easing: 'easeOutQuart' }
          }
        });
      }

      // ── Chart 3: Completion Rate — Doughnut with high cutout ─────────
      if (passFailChartRef.current) {
        const ctx = passFailChartRef.current.getContext('2d');
        const employees = users.filter(u => u.role === 'employee');
        let approvals = 0;
        for (const u of employees) {
          const pu = progressUsers.find(p => p.user_id === u.id) || { summary: { total_courses: 0, completed: 0 } };
          const total = pu.summary?.total_courses || 0;
          const done = pu.summary?.completed || 0;
          if (total > 0 && done >= total) approvals += 1;
        }
        const fails = Math.max(employees.length - approvals, 0);
        passFailChartInstance.current = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: ['Concluíram', 'Em progresso'],
            datasets: [{
              data: [approvals, fails || (employees.length === 0 ? 1 : 0)],
              backgroundColor: ['rgba(34,197,94,0.75)', 'rgba(255,255,255,0.07)'],
              hoverBackgroundColor: ['rgba(34,197,94,0.92)', 'rgba(255,255,255,0.12)'],
              borderWidth: 0,
              hoverOffset: 4,
            }]
          },
          options: {
            cutout: '74%',
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: '#5a5a7a',
                  font: { size: 11, family: "'Inter', sans-serif" },
                  padding: 18,
                  usePointStyle: true,
                  pointStyleWidth: 8,
                }
              },
              tooltip: {
                ...ttStyle,
                callbacks: { label: ctx => `  ${ctx.parsed} ${ctx.label}` }
              }
            },
            maintainAspectRatio: false,
            responsive: true,
            animation: { duration: 700, easing: 'easeOutQuart' }
          }
        });
      }
    }, 0);

    return () => clearTimeout(t);
  }, [activeTab, selectedStudent, sectorCounts, courses, completionByCourse, users, progressUsers]);

  const exportCsv = (people, progress) => {
    const rows = [];
    rows.push(['Nome','Setor','CursosConcluidos/Total','Avaliacoes','Media','Aprovado']);
    for (const u of people) {
      const pu = progress.find(p => p.user_id === u.id) || { summary: { total_courses: 0, completed: 0 } };
      const total = pu.summary?.total_courses || 0;
      const done = pu.summary?.completed || 0;
      const approved = total > 0 && done >= total ? 'Sim' : 'Não';
      rows.push([u.name, u.sector, `${done}/${total}`, '-', '-', approved]);
    }
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'relatorio-colaboradores-escola-suporti.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    
    if (!courseForm.title || !courseForm.description) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (!courseForm.external_url && courseForm.segments.length === 0 && !newSegment.title) {
      toast.error("Adicione pelo menos um módulo ao curso");
      return;
    }
    
    if (courseForm.sectors.length === 0) {
      toast.error("Selecione pelo menos um setor");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Adicionar automaticamente newSegment se tiver vídeo mas não foi adicionado ainda
      let finalSegments = [...courseForm.segments];
      if (newSegment.title && (newSegment.video_id || newSegment.video_url)) {
        // Verificar se não é duplicado
        const isDuplicate = finalSegments.some(seg => 
          seg.title === newSegment.title && 
          (seg.video_id === newSegment.video_id || seg.video_url === newSegment.video_url)
        );
        
        if (!isDuplicate) {
          finalSegments.push({ ...newSegment, id: uuidv4() });
          console.log("Adicionando newSegment automaticamente:", newSegment);
        }
      }
      
      // Se não há segmentos e é um curso NOVO (sem ID), criar automaticamente baseado na URL externa
      if (finalSegments.length === 0 && !editingCourse) {
        finalSegments = [{
          id: uuidv4(),
          title: courseForm.title,
          description: courseForm.description,
          video_url: courseForm.external_url,
          video_type: "youtube",
          duration_minutes: 0,
          order: 1
        }];
        console.log("Criando segmento automático:", finalSegments[0]);
      }
      
      const courseData = {
        ...courseForm,
        segments: finalSegments.map((seg, index) => ({
          id: seg.id,
          title: seg.title || "",
          description: seg.description || "",
          video_url: seg.video_url || null,
          video_id: seg.video_id || null,
          video_type: seg.video_type || "youtube",
          duration_minutes: seg.duration_minutes || 0,
          order: index + 1
        })),
        trail_order: courseForm.trail_order || 0,
        requires_assessment: courseForm.requires_assessment || false,
        linked_assessment_id: courseForm.linked_assessment_id || null,
        passing_score_required: courseForm.passing_score_required || 70.0
      };
      
      console.log("Dados do curso sendo enviados:", courseData);
      
      const shouldGenerateAssessment = courseForm.requires_assessment;
      let savedCourseId = editingCourse?.id;

      if (editingCourse) {
        // Update existing course
        await axios.put(`${API}/courses/${editingCourse.id}`, courseData, { headers });
        toast.success("Curso atualizado com sucesso!");
        setCourses(prevCourses => 
          prevCourses.map(course => 
            course.id === editingCourse.id ? { ...course, ...courseData } : course
          )
        );
      } else {
        // Create new course
        const { data } = await axios.post(`${API}/courses`, courseData, { headers });
        savedCourseId = data?.id || savedCourseId;
        toast.success("Curso criado com sucesso!");
      }

      if (shouldGenerateAssessment) {
        await handleGenerateCourseAssessmentWithAI(savedCourseId, finalSegments);
      }
      
      // Reset form and close dialog
      setCourseForm({
        title: "",
        description: "",
        external_url: "",
        thumbnail_url: "",
        sectors: [],
        segments: [],
        trail_order: 0,
        requires_assessment: false,
        linked_assessment_id: null,
        passing_score_required: 70.0
      });
      setCourseDialogOpen(false);
      setEditingCourse(null);
      
      // Refresh data
      fetchAdminData();
      
    } catch (error) {
      console.error("Error creating/updating course:", error);
      toast.error(getErrorMessage(error, "Erro ao salvar curso"));
    }
  };

  const handleGenerateCourseAssessmentWithAI = async (courseId, segmentsToCheck) => {
    if (!courseId) {
      toast.error("Salve o curso antes de gerar a avaliação.");
      return;
    }
    if (!segmentsToCheck?.length) {
      toast.error("Adicione pelo menos um módulo antes de gerar a avaliação.");
      return;
    }

    const numQuestions = Math.min(
      25,
      Math.max(1, parseInt(courseAssessmentAIConfig.num_questions, 10) || 10)
    );

    setCourseAssessmentAIConfig(prev => ({ ...prev, num_questions: String(numQuestions) }));
    setGeneratingCourseAssessment(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { num_questions: numQuestions };
      const { data } = await axios.post(
        `${API}/courses/${courseId}/generate-ai-assessment`,
        payload,
        { headers }
      );

      toast.success(data.message || "Avaliação gerada com IA!");

      if (data.assessment_id) {
        setCourseForm(prev => ({
          ...prev,
          requires_assessment: true,
          linked_assessment_id: data.assessment_id
        }));
      }

      if (Array.isArray(data.missing_segments) && data.missing_segments.length > 0) {
        const previewTitles = data.missing_segments
          .slice(0, 2)
          .map(seg => seg.title)
          .filter(Boolean)
          .join(", ");
        toast.info(
          `Ignoramos ${data.missing_segments.length} módulo(s) sem transcrição${previewTitles ? ` (${previewTitles}${data.missing_segments.length > 2 ? ", ..." : ""})` : ""}.`
        );
      }

      fetchAdminData();
    } catch (error) {
      console.error("Erro ao gerar avaliação do curso com IA:", error);
      toast.error(getErrorMessage(error, "Erro ao gerar avaliação do curso com IA"));
    } finally {
      setGeneratingCourseAssessment(false);
    }
  };

  const handleEditCourse = (course) => {
    setCourseForm({
      title: course.title,
      description: course.description,
      external_url: course.external_url,
      thumbnail_url: course.thumbnail_url || "",
      sectors: course.sectors,
      segments: course.segments || [],
      trail_order: course.trail_order || 0,
      requires_assessment: course.requires_assessment || false,
      linked_assessment_id: course.linked_assessment_id || null,
      passing_score_required: course.passing_score_required || 70.0
    });
    setEditingCourse(course);
    setCourseDialogOpen(true);
  };

  const handleDeleteCourse = (courseId, courseTitle) => {
    openConfirmDialog(
      'Excluir Curso',
      `Tem certeza que deseja excluir o curso "${courseTitle}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          await axios.delete(`${API}/courses/${courseId}`, { headers });
          toast.success("Curso excluído com sucesso!");
          fetchAdminData();
        } catch (error) {
          console.error("Error deleting course:", error);
          toast.error("Erro ao excluir curso");
        }
      }
    );
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const headers = { 
        Authorization: `Bearer ${token}`,
      };
      
      const response = await axios.post(`${API}/upload-image`, formData, { headers });
      
      // Construct full URL for the image
      const imageUrl = `${BACKEND_URL}${response.data.file_url}`;
      
      // Update course form with image URL
      setCourseForm(prev => ({
        ...prev,
        thumbnail_url: imageUrl
      }));
      
      console.log("Image uploaded successfully:", imageUrl);
      toast.success("Imagem enviada com sucesso!");
      
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Erro ao enviar imagem");
    } finally {
      setImageUploading(false);
    }
  };

  const resetUserForm = () => {
    setEditingUser(null);
    setUserForm({
      name: "",
      email: "",
      password: "",
      sector: "",
      role: "employee",
      phone: "",
      address: "",
      code: "",
      shift: "",
      period: "",
      modality: "",
      start_date: "",
      status: "",
      institution: "",
      education_level: "",
      mentor: ""
    });
  };

  const handleEditUser = (userToEdit) => {
    console.log('Carregando usuário para edição:', userToEdit);
    setEditingUser(userToEdit);
    setUserForm({
      name: userToEdit.name,
      email: userToEdit.email,
      password: "", // Deixe a senha em branco. O usuário digitará se quiser mudar.
      sector: userToEdit.sector,
      role: userToEdit.role,
      phone: userToEdit.phone || "",
      address: userToEdit.address || "",
      code: userToEdit.code || "",
      shift: userToEdit.shift || "",
      period: userToEdit.period || "",
      modality: userToEdit.modality || "",
      start_date: userToEdit.start_date || "",
      status: userToEdit.status || "",
      institution: userToEdit.institution || "",
      education_level: userToEdit.education_level || "",
      mentor: userToEdit.mentor || ""
    });
    console.log('Formulário preenchido com:', {
      phone: userToEdit.phone,
      address: userToEdit.address,
      code: userToEdit.code,
      shift: userToEdit.shift
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    
    // Validação principal
    if (!userForm.name || !userForm.email || !userForm.sector) {
      toast.error("Nome, email e setor são obrigatórios");
      return;
    }

    // Validação de senha: só é obrigatória se for um NOVO usuário
    if (!editingUser && !userForm.password) {
      toast.error("Senha é obrigatória ao criar um novo usuário");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingUser) {
        // --- LÓGICA DE ATUALIZAÇÃO (UPDATE) ---
        const updateData = {
          name: userForm.name,
          email: userForm.email,
          sector: userForm.sector,
          role: userForm.role,
          // Campos adicionais de perfil
          phone: userForm.phone,
          address: userForm.address,
          code: userForm.code,
          shift: userForm.shift,
          period: userForm.period,
          modality: userForm.modality,
          start_date: userForm.start_date,
          status: userForm.status,
          institution: userForm.institution,
          education_level: userForm.education_level,
          mentor: userForm.mentor,
        };

        // Só envie a senha se o usuário digitou uma nova
        if (userForm.password) {
          updateData.password = userForm.password;
        }

        console.log('Enviando dados de atualização:', updateData);
        
        // Assumindo que sua rota de update é /admin/users/{id} (igual ao delete)
        await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, { headers });
        toast.success("Usuário atualizado com sucesso!");

      } else {
        // --- LÓGICA DE CRIAÇÃO (CREATE) ---
        const createData = {
          ...userForm,
          // Garante role correto: se em systemUserMode e role ainda for employee, usa coordinator
          role: systemUserMode && userForm.role === 'employee' ? 'coordinator' : userForm.role,
        };
        console.log('Criando novo usuário:', createData);
        await axios.post(`${API}/auth/register`, createData, { headers });
        toast.success("Usuário criado com sucesso!");
      }

      // Resetar form e fechar dialog
      setUserDialogOpen(false);
      setSystemUserMode(false);
      resetUserForm();
      
      // Recarregar dados
      fetchAdminData();
      
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(getErrorMessage(error, "Erro ao salvar usuário"));
    }
  };


  const resetSectorForm = () => {
    setEditingSector(null);
    setSectorForm({
      name: "",
      display_name: "",
      description: ""
    });
  };

  const handleEditSector = (sectorToEdit) => {
    setEditingSector(sectorToEdit);
    setSectorForm({
      // Não editamos o 'name' (identificador)
      name: sectorToEdit.name, // Mantém o nome original no form, mas desabilitado
      display_name: sectorToEdit.display_name,
      description: sectorToEdit.description || ""
      // Adicione is_active se quiser poder editar isso
    });
    setSectorDialogOpen(true);
  };

  const handleSaveSector = async (e) => {
    e.preventDefault();

    // Validação (o 'name' só é validado na criação pois está desabilitado na edição)
    if (!sectorForm.name || !sectorForm.display_name) {
      toast.error("Nome do setor (identificador) e Nome de Exibição são obrigatórios");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };

      if (editingSector) {
        // --- LÓGICA DE ATUALIZAÇÃO (UPDATE) ---
        // Enviamos apenas os campos que podem ser editados
        const updateData = {
          display_name: sectorForm.display_name,
          description: sectorForm.description,
          // is_active: sectorForm.is_active // Descomente se adicionou o checkbox
        };

        // Usamos a rota PUT com o ID do setor
        await axios.put(`${API}/admin/sectors/${editingSector.id}`, updateData, { headers });
        toast.success("Setor atualizado com sucesso!");

      } else {
        // --- LÓGICA DE CRIAÇÃO (CREATE) ---
        // Enviamos todos os dados do formulário
        await axios.post(`${API}/admin/sectors`, sectorForm, { headers });
        toast.success("Setor criado com sucesso!");
      }

      // Resetar form e fechar dialog
      setSectorDialogOpen(false); 
      resetSectorForm(); // Usa a nova função de reset

      // Recarregar dados
      fetchAdminData();

    } catch (error) {
      console.error("Error saving sector:", error);
      toast.error(getErrorMessage(error, "Erro ao salvar setor"));
    }
  };

  const handleUserApproval = async (userId, approved, sector = null) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/admin/approve-user`, {
        user_id: userId,
        approved: approved,
        sector: sector
      }, { headers });
      
      toast.success(`Usuário ${approved ? 'aprovado' : 'rejeitado'} com sucesso!`);
      
      // Refresh data
      fetchAdminData();
      
    } catch (error) {
      console.error("Error approving user:", error);
      toast.error(getErrorMessage(error, "Erro ao processar aprovação"));
    }
  };

  const handleDeleteUser = (userId, userName) => {
    openConfirmDialog(
      'Excluir Usuário',
      `Tem certeza que deseja excluir o usuário "${userName}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          await axios.delete(`${API}/admin/users/${userId}`, { headers });
          toast.success("Usuário excluído com sucesso!");
          fetchAdminData();
        } catch (error) {
          console.error("Error deleting user:", error);
          toast.error("Erro ao excluir usuário");
        }
      }
    );
  };
  const handleUpdateSettings = async (e) => {
    e.preventDefault();

    try {
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(`${API}/admin/settings`, settingsForm, { headers });
      toast.success("Configurações atualizadas com sucesso!");

      // Refresh data
      fetchAdminData();

    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Erro ao atualizar configurações");
    }
  };

  const handleUpdateAdminProfile = async (e) => {
    e.preventDefault();
    setAdminProfileLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const updateData = {};
      Object.keys(adminProfileForm).forEach(key => {
        if (adminProfileForm[key] && adminProfileForm[key].trim() !== "") {
          updateData[key] = adminProfileForm[key].trim();
        }
      });
      await axios.put(`${API}/auth/me`, updateData, { headers });
      toast.success("Perfil atualizado com sucesso!");
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      toast.error(getErrorMessage(error, "Erro ao atualizar perfil"));
    } finally {
      setAdminProfileLoading(false);
    }
  };

  // Assessment management functions
  const handleCreateAssessment = async (e) => {
    e.preventDefault();
    
    if (!assessmentForm.title || !assessmentForm.description) {
      toast.error("Preencha o título e descrição da avaliação");
      return;
    }

    if (questions.some(q => !q.question_text || q.options.some(opt => !opt.trim()))) {
      toast.error("Preencha todas as questões e opções");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const requestData = {
        assessment: {
          ...assessmentForm,
          sectors: assessmentForm.sectors.length > 0 ? assessmentForm.sectors : [user.sector]
        },
        questions: questions
      };
      
      if (editingAssessment) {
        // Update existing assessment
        await axios.put(`${API}/assessments/${editingAssessment.id}`, requestData, { headers });
        toast.success("Avaliação atualizada com sucesso!");
      } else {
        // Create new assessment
        await axios.post(`${API}/assessments/create`, requestData, { headers });
        toast.success("Avaliação criada com sucesso!");
      }
      
      // Reset form and close dialog
      resetAssessmentForm();
      setAssessmentDialogOpen(false);
      
      // Refresh data
      fetchAdminData();
      
    } catch (error) {
      console.error("Error saving assessment:", error);
      toast.error(getErrorMessage(error, "Erro ao salvar avaliação"));
    }
  };

  const resetAssessmentForm = () => {
    setAssessmentForm({
      title: "",
      description: "",
      course_id: "",
      time_limit: 60,
      passing_grade: 70.0,
      max_attempts: 3,
      cooldown_period: 24,
      sectors: []
    });
    setQuestions([
      {
        question_text: "",
        options: ["A) ", "B) ", "C) ", "D) "],
        correct_answer: "A",
        explanation: "",
        points: 1.0
      }
    ]);
    setAiGenerationData({
      url: "",
      description: "",
      num_questions: 10,
      assessment_title: ""
    });
    setHasAiGenerated(false);
    setEditingAssessment(null);
  };

  const handleEditAssessment = async (assessment) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      setHasAiGenerated(false);
      setAiGenerationData(prev => ({
        ...prev,
        url: "",
        description: "",
        assessment_title: "",
        num_questions: 10
      }));
      
      // Fetch assessment details including questions
      const response = await axios.get(`${API}/assessments/${assessment.id}`, { headers });
      const assessmentData = response.data;
      
      // Populate form with existing data
      setAssessmentForm({
        title: assessmentData.assessment.title,
        description: assessmentData.assessment.description,
        course_id: assessmentData.assessment.course_id || "",
        time_limit: assessmentData.assessment.time_limit,
        passing_grade: assessmentData.assessment.passing_grade,
        max_attempts: assessmentData.assessment.max_attempts,
        cooldown_period: assessmentData.assessment.cooldown_period,
        sectors: assessmentData.assessment.sectors || []
      });
      
      // Populate questions
      setQuestions(assessmentData.questions || []);
      
      // Set editing mode
      setEditingAssessment(assessment);
      setAssessmentDialogOpen(true);
      
      toast.success("Dados da avaliação carregados para edição");
      
    } catch (error) {
      console.error("Error loading assessment for editing:", error);
      toast.error("Erro ao carregar dados da avaliação");
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index][field] = value;
    setQuestions(updatedQuestions);
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex] = value;
    setQuestions(updatedQuestions);
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        question_text: "",
        options: ["A) ", "B) ", "C) ", "D) "],
        correct_answer: "A",
        explanation: "",
        points: 1.0
      }
    ]);
  };

  const removeQuestion = (index) => {
    if (questions.length > 1) {
      const updatedQuestions = questions.filter((_, i) => i !== index);
      setQuestions(updatedQuestions);
    }
  };

  const generateQuestionsFromAI = async () => {
    if (!aiGenerationData.assessment_title) {
      toast.error("Informe o título da avaliação");
      return;
    }
    if (!aiGenerationData.url && !aiGenerationData.description) {
      toast.error("Forneça um link de artigo ou uma descrição detalhada");
      return;
    }

    setGeneratingQuestions(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        url: aiGenerationData.url?.trim() || null,
        description: aiGenerationData.description?.trim() || null,
        num_questions: aiGenerationData.num_questions,
        assessment_title: aiGenerationData.assessment_title
      };
      
      const response = await axios.post(`${API}/questions/generate`, payload, { headers });

      const aiQuestions = response.data.questions.map(q => ({
        question_text: q.question_text,
        options: q.options,
        correct_answer: q.correct_answer,
        explanation: q.explanation || "",
        points: 1.0
      }));

      setQuestions(aiQuestions);
      setAssessmentForm(prev => ({
        ...prev,
        title: aiGenerationData.assessment_title
      }));
      setHasAiGenerated(true);
      toast.success("Questões geradas com sucesso!");
      setActiveAssessmentTab("ai");
    } catch (error) {
      console.error("Error generating questions:", error);
      toast.error(error.response?.data?.detail || "Erro ao gerar questões com a IA.");
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const generateAdditionalAIQuestion = async () => {
    if (!hasAiGenerated) {
      toast.error("Gere as primeiras questões antes de adicionar novas.");
      return;
    }
    if (!aiGenerationData.url && !aiGenerationData.description) {
      toast.error("Forneça o conteúdo de referência para a IA.");
      return;
    }
    setGeneratingQuestions(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        url: aiGenerationData.url?.trim() || null,
        description: aiGenerationData.description?.trim() || null,
        num_questions: 1,
        assessment_title: aiGenerationData.assessment_title || assessmentForm.title || "Avaliação"
      };
      const response = await axios.post(`${API}/questions/generate`, payload, { headers });
      const aiQuestions = response.data.questions.map(q => ({
        question_text: q.question_text,
        options: q.options,
        correct_answer: q.correct_answer,
        explanation: q.explanation || "",
        points: 1.0
      }));
      setQuestions(prev => [...prev, ...aiQuestions]);
      setHasAiGenerated(true);
      toast.success("Nova pergunta adicionada!");
    } catch (error) {
      console.error("Error generating extra question:", error);
      toast.error(error.response?.data?.detail || "Erro ao gerar a nova pergunta.");
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const parseTextQuestions = async () => {
    if (!textImportData.text || !textImportData.assessment_title) {
      toast.error("Preencha o texto e o título da avaliação");
      return;
    }

    setGeneratingQuestions(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/questions/parse-text`, {
        text: textImportData.text,
        assessment_title: textImportData.assessment_title
      }, { headers });

      const parsedQuestions = response.data.questions.map(q => ({
        question_text: q.question_text,
        options: q.options,
        correct_answer: q.correct_answer,
        explanation: q.explanation || "",
        points: q.points || 1.0
      }));

      setQuestions(parsedQuestions);
      setAssessmentForm(prev => ({
        ...prev,
        title: textImportData.assessment_title
      }));
      
      toast.success("Questões processadas com sucesso!");
      setActiveAssessmentTab("manual");
    } catch (error) {
      console.error("Error parsing text questions:", error);
      toast.error("Erro ao processar texto. Verifique o formato e tente novamente.");
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const handleDeleteAssessment = (assessmentId, assessmentTitle) => {
    openConfirmDialog(
      'Excluir Avaliação',
      `Tem certeza que deseja excluir a avaliação "${assessmentTitle}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          await axios.delete(`${API}/assessments/${assessmentId}`, { headers });
          toast.success("Avaliação excluída com sucesso!");
          fetchAdminData();
        } catch (error) {
          console.error("Error deleting assessment:", error);
          toast.error("Erro ao excluir avaliação");
        }
      }
    );
  };

  const addSegment = () => {
    if (!newSegment.title) {
      toast.error("Título é obrigatório");
      return;
    }

    // Validar que pelo menos uma fonte de vídeo foi fornecida
    if (!newSegment.video_url && !newSegment.video_id) {
      toast.error("Forneça um vídeo (upload ou URL do YouTube)");
      return;
    }

    setCourseForm(prev => ({
      ...prev,
      segments: [...prev.segments, { ...newSegment, id: uuidv4() }]
    }));

    setNewSegment({
      title: "",
      description: "",
      video_url: "",
      video_id: null,
      duration_minutes: 0,
      order: courseForm.segments.length + 2,
      video_type: "local"
    });
  };

  const removeSegment = (index) => {
    setCourseForm(prev => ({
      ...prev,
      segments: prev.segments.filter((_, i) => i !== index)
    }));
  };

  const getSectorDisplayName = (sectorName) => {
    const sector = sectors.find(s => s.name === sectorName);
    return sector ? sector.display_name : sectorName;
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'employee': 'Colaborador',
      'coordinator': 'Coordenador',
      'admin': 'Administrador'
    };
    return roleNames[role] || role;
  };

  const handleViewReport = async (userId) => {
    setReportData(null); // Limpa dados antigos
    setReportLoading(true);
    setReportModalOpen(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/reports/student/${userId}`, { headers });
      setReportData(response.data);
    } catch (error) {
      console.error("Erro ao buscar relatório do aluno:", error);
      toast.error("Não foi possível carregar o relatório.");
      setReportModalOpen(false); // Fecha o modal em caso de erro
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando painel administrativo...</p>
        </div>
      </div>
    );
  }

  const goToCadastros = (tab) => {
    setCadastrosTab(tab);
    setActiveTab(tab);
  };

  return (
    <div className="dashboard-layout">
      {/* ── Admin Sidebar ── */}
      <aside className={`sidebar${adminSidebarCollapsed ? ' collapsed' : ''}`} style={{
        background: '#0d0d14',
        borderRight: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        flexDirection: 'column',
        width: adminSidebarCollapsed ? 60 : 220,
        height: '100vh',
        overflowY: 'auto',
        transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
        position: 'sticky',
        top: 0,
        flexShrink: 0,
      }}>
        {/* Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          padding: adminSidebarCollapsed ? '1.1rem 0' : '1.1rem 1.1rem',
          justifyContent: adminSidebarCollapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          minHeight: 60,
        }}>
          <img src={IconeEscola} alt="Escola de Suporti" style={{ height: 32, width: 32, objectFit: 'contain', flexShrink: 0, borderRadius: 8 }} />
          {!adminSidebarCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.85rem', fontWeight: 700, color: '#f1f1f3', letterSpacing: '-0.01em' }}>Escola de Suporti</span>
              <span style={{ fontSize: '0.65rem', color: '#4a4a6a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Admin</span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '1rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {/* Section label */}
          {!adminSidebarCollapsed && (
            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.12em', padding: '0.25rem 0.6rem 0.5rem' }}>Principal</span>
          )}

          {/* Dashboard */}
          <button
            className={`sidebar-item${activeTab === 'overview' ? ' active' : ''}`}
            onClick={() => setActiveTab('overview')}
            data-testid="overview-tab"
            title={adminSidebarCollapsed ? 'Dashboard' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: adminSidebarCollapsed ? '0.6rem 0' : '0.6rem 0.75rem',
              justifyContent: adminSidebarCollapsed ? 'center' : 'flex-start',
              borderRadius: 8,
              border: 'none',
              background: activeTab === 'overview' ? 'rgba(99,102,241,0.12)' : 'transparent',
              color: activeTab === 'overview' ? '#818cf8' : '#5a5a7a',
              cursor: 'pointer',
              width: '100%',
              position: 'relative',
              fontFamily: "'Inter', sans-serif",
              fontSize: '0.82rem',
              fontWeight: activeTab === 'overview' ? 600 : 400,
              transition: 'all 0.15s ease',
            }}
          >
            {activeTab === 'overview' && (
              <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 18, background: '#6366f1', borderRadius: '0 3px 3px 0' }} />
            )}
            <BarChart3 size={16} />
            {!adminSidebarCollapsed && <span>Dashboard</span>}
          </button>

          {/* Cadastros */}
          <button
            className={`sidebar-item${inCadastros ? ' active' : ''}`}
            onClick={() => goToCadastros(cadastrosTab)}
            data-testid="cadastros-tab"
            title={adminSidebarCollapsed ? 'Cadastros' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: adminSidebarCollapsed ? '0.6rem 0' : '0.6rem 0.75rem',
              justifyContent: adminSidebarCollapsed ? 'center' : 'flex-start',
              borderRadius: 8, border: 'none',
              background: inCadastros ? 'rgba(99,102,241,0.12)' : 'transparent',
              color: inCadastros ? '#818cf8' : '#5a5a7a',
              cursor: 'pointer', width: '100%', position: 'relative',
              fontFamily: "'Inter', sans-serif", fontSize: '0.82rem',
              fontWeight: inCadastros ? 600 : 400, transition: 'all 0.15s ease',
            }}
          >
            {inCadastros && (
              <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 18, background: '#6366f1', borderRadius: '0 3px 3px 0' }} />
            )}
            <ClipboardList size={16} />
            {!adminSidebarCollapsed && <span>Cadastros</span>}
          </button>

          {/* Section label */}
          {!adminSidebarCollapsed && (
            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.12em', padding: '0.75rem 0.6rem 0.5rem' }}>Gestão</span>
          )}
          {adminSidebarCollapsed && <div style={{ height: 12 }} />}

          {/* Aprovações */}
          <button
            className={`sidebar-item${activeTab === 'approvals' ? ' active' : ''}`}
            onClick={() => setActiveTab('approvals')}
            data-testid="approvals-tab"
            title={adminSidebarCollapsed ? 'Aprovações' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: adminSidebarCollapsed ? '0.6rem 0' : '0.6rem 0.75rem',
              justifyContent: adminSidebarCollapsed ? 'center' : 'flex-start',
              borderRadius: 8, border: 'none',
              background: activeTab === 'approvals' ? 'rgba(99,102,241,0.12)' : 'transparent',
              color: activeTab === 'approvals' ? '#818cf8' : '#5a5a7a',
              cursor: 'pointer', width: '100%', position: 'relative',
              fontFamily: "'Inter', sans-serif", fontSize: '0.82rem',
              fontWeight: activeTab === 'approvals' ? 600 : 400, transition: 'all 0.15s ease',
            }}
          >
            {activeTab === 'approvals' && (
              <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 18, background: '#6366f1', borderRadius: '0 3px 3px 0' }} />
            )}
            <UserCheck size={16} />
            {!adminSidebarCollapsed && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1 }}>
                Aprovações
                {pendingUsers.length > 0 && (
                  <span style={{ background: '#ef4444', color: '#fff', fontSize: '0.6rem', fontWeight: 700, padding: '1px 6px', borderRadius: 999, lineHeight: '16px', marginLeft: 'auto' }}>
                    {pendingUsers.length}
                  </span>
                )}
              </span>
            )}
            {adminSidebarCollapsed && pendingUsers.length > 0 && (
              <span style={{ position: 'absolute', top: 6, right: 6, width: 7, height: 7, borderRadius: '50%', background: '#ef4444' }} />
            )}
          </button>

          {/* Configurações */}
          <button
            className={`sidebar-item${activeTab === 'settings' ? ' active' : ''}`}
            onClick={() => setActiveTab('settings')}
            data-testid="settings-tab"
            title={adminSidebarCollapsed ? 'Configurações' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: adminSidebarCollapsed ? '0.6rem 0' : '0.6rem 0.75rem',
              justifyContent: adminSidebarCollapsed ? 'center' : 'flex-start',
              borderRadius: 8, border: 'none',
              background: activeTab === 'settings' ? 'rgba(99,102,241,0.12)' : 'transparent',
              color: activeTab === 'settings' ? '#818cf8' : '#5a5a7a',
              cursor: 'pointer', width: '100%', position: 'relative',
              fontFamily: "'Inter', sans-serif", fontSize: '0.82rem',
              fontWeight: activeTab === 'settings' ? 600 : 400, transition: 'all 0.15s ease',
            }}
          >
            {activeTab === 'settings' && (
              <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 18, background: '#6366f1', borderRadius: '0 3px 3px 0' }} />
            )}
            <Settings size={16} />
            {!adminSidebarCollapsed && <span>Configurações</span>}
          </button>
        </nav>

        {/* Footer */}
        <div style={{ padding: '0.75rem 0.5rem', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Link to="/dashboard" className="sidebar-item" title={adminSidebarCollapsed ? 'Ver como aluno' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: adminSidebarCollapsed ? '0.6rem 0' : '0.6rem 0.75rem',
              justifyContent: adminSidebarCollapsed ? 'center' : 'flex-start',
              borderRadius: 8, color: '#4a4a6a',
              fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', textDecoration: 'none',
              transition: 'all 0.15s ease',
            }}
          >
            <Home size={16} />
            {!adminSidebarCollapsed && <span>Ver como aluno</span>}
          </Link>
          <button
            className="sidebar-item"
            onClick={logout}
            title={adminSidebarCollapsed ? 'Sair' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: adminSidebarCollapsed ? '0.6rem 0' : '0.6rem 0.75rem',
              justifyContent: adminSidebarCollapsed ? 'center' : 'flex-start',
              borderRadius: 8, border: 'none', background: 'transparent',
              color: '#6b3030', cursor: 'pointer', width: '100%',
              fontFamily: "'Inter', sans-serif", fontSize: '0.82rem',
              transition: 'all 0.15s ease',
            }}
          >
            <LogOut size={16} />
            {!adminSidebarCollapsed && <span>Sair</span>}
          </button>

          {/* Collapse toggle */}
          <button
            className="sidebar-toggle"
            onClick={() => setAdminSidebarCollapsed(v => !v)}
            style={{
              marginTop: '0.25rem', display: 'flex', alignItems: 'center',
              justifyContent: adminSidebarCollapsed ? 'center' : 'flex-end',
              gap: '0.35rem', padding: '0.5rem 0.75rem',
              borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)',
              background: 'rgba(255,255,255,0.02)', color: '#3a3a5a',
              cursor: 'pointer', fontSize: '0.72rem', width: '100%',
              fontFamily: "'Inter', sans-serif", transition: 'all 0.15s ease',
            }}
          >
            {adminSidebarCollapsed
              ? <ChevronRight size={14} />
              : <><ChevronLeft size={14} /><span>Recolher</span></>}
          </button>
        </div>
      </aside>

      {/* ── Conteúdo principal ── */}
      <div className="dashboard-content" style={{ background: '#0a0a0f', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Top Bar */}
        <header style={{
          background: '#0d0d14',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          padding: '0 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 60,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 40,
        }}>
          {/* Left: page title */}
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.95rem', fontWeight: 700, color: '#e8e8f0', margin: 0, letterSpacing: '-0.01em' }}>Painel Administrativo</h1>
            <p style={{ fontSize: '0.7rem', color: '#3a3a5a', margin: 0, fontFamily: "'Inter', sans-serif" }}>Gestão de Cursos e Usuários</p>
          </div>

          {/* Center: Search */}
          <div style={{ position: 'relative', width: 280 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#3a3a5a', pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Buscar cursos, usuários..."
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8,
                padding: '0.45rem 0.75rem 0.45rem 2.1rem',
                color: '#9a9ab0',
                fontSize: '0.8rem',
                fontFamily: "'Inter', sans-serif",
                outline: 'none',
              }}
              readOnly
            />
          </div>

          {/* Right: actions + user */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Bell */}
            <button style={{
              width: 34, height: 34, borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)',
              background: 'rgba(255,255,255,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#4a4a6a', position: 'relative', flexShrink: 0,
            }}>
              <Bell size={15} />
              {pendingUsers.length > 0 && (
                <span style={{ position: 'absolute', top: 5, right: 5, width: 7, height: 7, borderRadius: '50%', background: '#f97316', border: '2px solid #0d0d14' }} />
              )}
            </button>

            {/* Divider */}
            <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.06)' }} />

            {/* User */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700, color: '#fff',
                fontFamily: "'Space Grotesk', sans-serif",
              }}>
                {user.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#c8c8d8', fontFamily: "'Inter', sans-serif" }}>{user.name?.split(' ')[0]}</span>
                <span style={{ fontSize: '0.65rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif" }}>{getRoleDisplayName(user.role)}</span>
              </div>
            </div>
          </div>
        </header>

      {/* Sub-nav de Cadastros */}
      {inCadastros && (
        <div style={{
          background: '#0d0d14',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          padding: '0 1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          height: 48,
        }}>
          {[
            { tab: 'users', icon: <Users size={13} />, label: 'Usuários' },
            { tab: 'courses', icon: <BookOpen size={13} />, label: 'Cursos & Trilhas' },
            { tab: 'assessments', icon: <Target size={13} />, label: 'Avaliações' },
            { tab: 'sectors', icon: <Route size={13} />, label: 'Setores' },
          ].map(({ tab, icon, label }) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => goToCadastros(tab)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.35rem 0.85rem',
                  borderRadius: 6,
                  border: isActive ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
                  background: isActive ? 'rgba(99,102,241,0.12)' : 'transparent',
                  color: isActive ? '#818cf8' : '#4a4a6a',
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontWeight: isActive ? 600 : 400,
                  fontFamily: "'Inter', sans-serif",
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {icon}
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Main Content */}
      <main style={{ width: '100%', padding: '1.5rem 1.5rem 2rem' }}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* TabsList removida — navegação agora na sidebar */}
          <div />

          <TabsContent value="overview" className="space-y-6 mt-6">
            {selectedStudent && activeTab === 'overview' ? (
              renderStudentProfile()
            ) : (
              <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              {/* Users */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Usuários</span>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Users size={13} style={{ color: '#3b82f6' }} />
                  </div>
                </div>
                <div>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{analytics.total_users || 0}</span>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>Total cadastrados</p>
                </div>
              </div>

              {/* Courses */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Cursos</span>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BookOpen size={13} style={{ color: '#22c55e' }} />
                  </div>
                </div>
                <div>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{analytics.total_courses || 0}</span>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>Ativos na plataforma</p>
                </div>
              </div>

              {/* Assessments */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Avaliações</span>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Target size={13} style={{ color: '#a855f7' }} />
                  </div>
                </div>
                <div>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{analytics.total_assessments || 0}</span>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>Total criadas</p>
                </div>
              </div>

              {/* Pass rate */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Aprovação</span>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Award size={13} style={{ color: '#f97316' }} />
                  </div>
                </div>
                <div>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{Math.round(analytics.pass_rate || 0)}%</span>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>Taxa geral</p>
                </div>
              </div>

              {/* Attempts */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Tentativas</span>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Activity size={13} style={{ color: '#94a3b8' }} />
                  </div>
                </div>
                <div>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.7rem', fontWeight: 700, color: '#e8e8f0', lineHeight: 1 }}>{analytics.total_attempts || 0}</span>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: '0.2rem 0 0' }}>Total realizadas</p>
                </div>
              </div>
            </div>

            {/* Charts */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">

              {/* ── Chart 1: Sector Distribution ── */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Users size={11} style={{ color: '#6366f1' }} />
                    </div>
                    <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.8rem', fontWeight: 600, color: '#c8c8d8', margin: 0 }}>Distribuição por Setor</h3>
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#3a3a5a', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: 4 }}>
                    {Object.values(sectorCounts).reduce((a,b)=>a+b,0)} total
                  </span>
                </div>
                <div style={{ height: Math.max(160, Object.keys(sectorCounts).length * 36) }}>
                  <canvas ref={sectorChartRef} />
                </div>
              </div>

              {/* ── Chart 2: Course Completion ── */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <BookOpen size={11} style={{ color: '#22c55e' }} />
                    </div>
                    <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.8rem', fontWeight: 600, color: '#c8c8d8', margin: 0 }}>Conclusão por Curso</h3>
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#3a3a5a', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: 4 }}>
                    top {Math.min(courses.length, 8)}
                  </span>
                </div>
                {/* Color legend */}
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.65rem', flexWrap: 'wrap' }}>
                  {[
                    { color: 'rgba(34,197,94,0.7)', label: '≥ 70%' },
                    { color: 'rgba(99,102,241,0.7)', label: '40–69%' },
                    { color: 'rgba(249,115,22,0.65)', label: '< 40%' },
                  ].map(({ color, label }) => (
                    <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.65rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif" }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: 'inline-block', flexShrink: 0 }} />
                      {label}
                    </span>
                  ))}
                </div>
                <div style={{ height: Math.max(160, Math.min(courses.length, 8) * 30) }}>
                  <canvas ref={courseCompletionChartRef} />
                </div>
              </div>

              {/* ── Chart 3: Completion Rate Doughnut ── */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CheckCircle2 size={11} style={{ color: '#22c55e' }} />
                    </div>
                    <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.8rem', fontWeight: 600, color: '#c8c8d8', margin: 0 }}>Taxa de Conclusão</h3>
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#3a3a5a', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: 4 }}>
                    {users.filter(u=>u.role==='employee').length} colaboradores
                  </span>
                </div>
                {/* Doughnut with center overlay */}
                <div style={{ position: 'relative', height: 200 }}>
                  <canvas ref={passFailChartRef} />
                  {/* Center text overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '42%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}>
                    {(() => {
                      const employees = users.filter(u => u.role === 'employee');
                      let approvals = 0;
                      for (const u of employees) {
                        const pu = progressUsers.find(p => p.user_id === u.id) || { summary: { total_courses: 0, completed: 0 } };
                        const total = pu.summary?.total_courses || 0;
                        const done = pu.summary?.completed || 0;
                        if (total > 0 && done >= total) approvals += 1;
                      }
                      const pct = employees.length > 0 ? Math.round((approvals / employees.length) * 100) : 0;
                      return (
                        <>
                          <div style={{ fontSize: '1.55rem', fontWeight: 800, color: '#22c55e', fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1, letterSpacing: '-0.03em' }}>
                            {pct}%
                          </div>
                          <div style={{ fontSize: '0.6rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif", marginTop: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            concluíram
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

            </section>

            {/* Quick Stats removed: Distribuição por Setor and Cursos Recentes no longer shown in Visão Geral */}

            {/* Cobertura por Curso moved to the end of Visão Geral */}

            {/* Cursos Recentes + Colaboradores */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Recent Courses */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
                  <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <BookOpen size={12} style={{ color: '#22c55e' }} />
                  </div>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#c8c8d8', margin: 0 }}>Cursos Recentes</h3>
                </div>
                <ul style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: 0, padding: 0, listStyle: 'none' }}>
                  {[...courses].sort((a,b)=> new Date(b.created_at||0)-new Date(a.created_at||0)).slice(0,5).map(c => (
                    <li key={c.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)',
                      borderRadius: 8, padding: '0.45rem 0.65rem',
                    }}>
                      <span style={{ fontSize: '0.78rem', color: '#a8a8c0', fontFamily: "'Inter', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{c.title || c.name || 'Curso'}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#3a3a5a', background: 'rgba(255,255,255,0.04)', padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>{c.segments?.length || 0} mod.</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Collaborators Table */}
              <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }} className="lg:col-span-2">
                {/* Table header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.85rem', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Users size={12} style={{ color: '#6366f1' }} />
                      </div>
                      <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#c8c8d8', margin: 0 }}>Colaboradores — Cursos &amp; Avaliações</h3>
                    </div>
                    {/* Filter pills */}
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {[
                        { key: 'all', label: 'Todos' },
                        { key: 'approved', label: 'Aprovados' },
                        { key: 'failed', label: 'Reprovados' },
                      ].map(f => (
                        <button
                          key={f.key}
                          type="button"
                          onClick={() => setPeopleFilter(f.key)}
                          style={{
                            padding: '3px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                            fontSize: '0.72rem', fontFamily: "'Inter', sans-serif", fontWeight: 500,
                            background: peopleFilter === f.key ? '#f97316' : 'rgba(255,255,255,0.04)',
                            color: peopleFilter === f.key ? '#fff' : '#4a4a6a',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
                    <button
                      type="button"
                      onClick={() => exportCsv(filteredPeople, progressUsers)}
                      style={{
                        padding: '4px 12px', borderRadius: 6, fontSize: '0.72rem',
                        fontFamily: "'Inter', sans-serif", fontWeight: 500, cursor: 'pointer',
                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                        color: '#6a6a8a', transition: 'all 0.15s ease',
                      }}
                    >
                      Exportar CSV
                    </button>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#3a3a5a' }}>{users.filter(u=>u.role==='employee').length} colaboradores</span>
                  </div>
                </div>

                {/* Table */}
                <div className="table-scroll" style={{ borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                  <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        {['Nome', 'Setor', 'Cursos', 'Avaliações', 'Média', 'Status'].map(h => (
                          <th key={h} style={{ padding: '0.55rem 0.75rem', textAlign: 'left', fontFamily: "'Inter', sans-serif", fontSize: '0.65rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPeople.map((u, idx) => {
                        const pu = progressUsers.find(p => p.user_id === u.id) || { summary: { total_courses: 0, completed: 0, passed_assessments: 0, total_assessments: 0 } };
                        const total = pu.summary?.total_courses || 0;
                        const done = pu.summary?.completed || 0;
                        const perc = total ? Math.round((done/total)*100) : 0;
                        const totalAssessments = pu.summary?.total_assessments || 0;
                        const passedAssessments = pu.summary?.passed_assessments || 0;
                        const approved = totalAssessments > 0 && passedAssessments >= totalAssessments;

                        // Sector color palette (cycle through)
                        const sectorColors = ['#6366f1','#22c55e','#f97316','#a855f7','#3b82f6','#ec4899','#14b8a6'];
                        const sectorColor = sectorColors[(u.sector || '').length % sectorColors.length];

                        return (
                          <tr
                            key={u.id}
                            onClick={() => loadStudent(u.id)}
                            style={{
                              background: idx % 2 === 0 ? '#0e0e16' : 'transparent',
                              borderBottom: '1px solid rgba(255,255,255,0.03)',
                              cursor: 'pointer',
                              transition: 'background 0.12s ease',
                              position: 'relative',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'rgba(99,102,241,0.06)';
                              e.currentTarget.style.borderLeft = '2px solid #6366f1';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = idx % 2 === 0 ? '#0e0e16' : 'transparent';
                              e.currentTarget.style.borderLeft = '';
                            }}
                          >
                            <td style={{ padding: '0.55rem 0.75rem', color: '#c8c8d8', fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>{u.name}</td>
                            <td style={{ padding: '0.55rem 0.75rem' }}>
                              <span style={{
                                display: 'inline-block', borderRadius: 5,
                                padding: '2px 8px', fontSize: '0.65rem', fontWeight: 600,
                                fontFamily: "'Inter', sans-serif",
                                background: `${sectorColor}18`,
                                color: sectorColor,
                                border: `1px solid ${sectorColor}30`,
                              }}>{getSectorDisplayName(u.sector) || u.sector}</span>
                            </td>
                            <td style={{ padding: '0.55rem 0.75rem' }}>
                              <span style={{ fontFamily: 'monospace', color: '#a8a8c0', fontSize: '0.75rem' }}>{done}/{total}</span>
                              <div style={{ width: '100%', background: 'rgba(255,255,255,0.06)', borderRadius: 999, height: 3, marginTop: 4 }}>
                                <div style={{ width: `${perc}%`, height: 3, borderRadius: 999, background: '#f97316', transition: 'width 0.3s ease' }} />
                              </div>
                            </td>
                            <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', color: '#a8a8c0', fontSize: '0.75rem' }}>{totalAssessments > 0 ? `${passedAssessments}/${totalAssessments}` : <span style={{ color: '#2a2a4a' }}>—</span>}</td>
                            <td style={{ padding: '0.55rem 0.75rem', fontFamily: 'monospace', color: '#a8a8c0', fontSize: '0.75rem' }}>{pu.summary?.average !== undefined ? Math.round(pu.summary.average) + '%' : <span style={{ color: '#2a2a4a' }}>—</span>}</td>
                            <td style={{ padding: '0.55rem 0.75rem' }}>
                              {totalAssessments > 0 ? (
                                approved
                                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 5, fontSize: '0.65rem', fontWeight: 600, background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)', fontFamily: "'Inter', sans-serif" }}><CheckCircle2 size={10} /> Aprovado</span>
                                  : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 5, fontSize: '0.65rem', fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)', fontFamily: "'Inter', sans-serif" }}><Minus size={10} /> Reprovado</span>
                              ) : <span style={{ color: '#2a2a4a', fontFamily: "'Inter', sans-serif", fontSize: '0.65rem' }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                      {filteredPeople.length === 0 && (
                        <tr><td colSpan={6} style={{ padding: '1.5rem 0.75rem', color: '#2a2a4a', fontFamily: "'Inter', sans-serif", fontSize: '0.78rem', textAlign: 'center' }}>Nenhum resultado encontrado.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Cobertura por Curso */}
            <section style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '1rem 1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(249,115,22,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <TrendingUp size={12} style={{ color: '#f97316' }} />
                  </div>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#c8c8d8', margin: 0 }}>Cobertura por Curso</h3>
                  <span style={{ fontSize: '0.68rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif" }}>— % de colaboradores que concluíram</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select
                    value={reportCourseId}
                    onChange={(e) => setReportCourseId(e.target.value)}
                    style={{
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 7, padding: '0.35rem 0.7rem', fontSize: '0.78rem',
                      color: '#9a9ab0', fontFamily: "'Inter', sans-serif", outline: 'none', width: 220,
                    }}
                  >
                    {courses.map(c => <option key={c.id} value={c.id}>{c.title || c.name}</option>)}
                  </select>
                  <span style={{
                    fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: '#f97316',
                    background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)',
                    padding: '3px 10px', borderRadius: 6, minWidth: 50, textAlign: 'center',
                  }}>{(() => {
                    const id = reportCourseId; if(!id) return '—';
                    const pct = completionByCourse[id] || 0; return `${pct}%`;
                  })()}</span>
                </div>
              </div>
              <div style={{ width: '100%', height: 4, background: 'rgba(255,255,255,0.05)', borderRadius: 999, marginTop: '0.85rem' }}>
                <div style={{
                  height: 4, borderRadius: 999,
                  background: 'linear-gradient(90deg, #f97316, #fb923c)',
                  transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
                  width: (() => {
                    const id = reportCourseId; if(!id) return '0%';
                    const pct = completionByCourse[id] || 0; return `${pct}%`;
                  })()
                }} />
              </div>
              <p style={{ color: '#3a3a5a', fontSize: '0.72rem', marginTop: '0.5rem', fontFamily: "'Inter', sans-serif" }}>{(() => {
                const id = reportCourseId; if(!id) return '—';
                const course = courses.find(c => c.id === id);
                const pct = completionByCourse[id] || 0;
                return `${pct}% dos colaboradores concluíram o curso "${course?.title || course?.name || 'Curso'}".`;
              })()}</p>
            </section>

              </>
            )}
          </TabsContent>

          <TabsContent value="courses" className="mt-0">
            {/* Course & Trail Management Header */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              gap: '1rem', flexWrap: 'wrap', paddingTop: '1.5rem', marginBottom: '1.25rem',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.2rem' }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: 'rgba(230,126,34,0.1)',
                    border: '1px solid rgba(230,126,34,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <BookOpen size={14} style={{ color: '#E67E22' }} />
                  </div>
                  <h2 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '1.08rem', fontWeight: 700,
                    color: '#e8e8f0', margin: 0, letterSpacing: '-0.01em',
                  }}>
                    Cursos &amp; Trilhas
                  </h2>
                </div>
                <p style={{
                  fontSize: '0.77rem', color: '#4a4a6a',
                  fontFamily: "'Inter', sans-serif", margin: 0, paddingLeft: '2.55rem',
                }}>
                  Organize cursos em trilhas de aprendizado por setor
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Dialog modal={!showVideoUploadManager} open={courseDialogOpen} onOpenChange={handleCourseDialogOpenChange}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={resetCourseForm}
                      data-testid="create-course-btn"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                        padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto',
                        background: 'linear-gradient(135deg, #E67E22, #CA6F1E)',
                        border: 'none', color: '#fff',
                        fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer',
                        fontFamily: "'Inter', sans-serif",
                        boxShadow: '0 2px 8px rgba(230,126,34,0.25)',
                        letterSpacing: '0.01em',
                      }}
                    >
                      <Plus size={14} />
                      Novo Curso
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
                    <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
                      <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
                        <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(231,126,34,0.12)', border: '1px solid rgba(231,126,34,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <BookOpen size={13} style={{ color: '#f39c12' }} />
                        </div>
                        {editingCourse ? 'Editar Curso' : 'Novo Curso'}
                      </DialogTitle>
                      <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
                        Preencha as informações do curso e adicione os módulos
                      </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateCourse} className="space-y-5 pt-1">

                    {/* ── Seção 1: Informações Básicas ── */}
                    <div className="space-y-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <BookOpen size={10} style={{ color: '#60a5fa' }} />
                        </div>
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Informações Básicas</span>
                      </div>

                      <div className="form-group">
                        <Label htmlFor="course-title" className="form-label">Título do Curso *</Label>
                        <Input
                          id="course-title"
                          value={courseForm.title}
                          onChange={(e) => setCourseForm(prev => ({ ...prev, title: e.target.value }))}
                          required
                          placeholder="Ex: Atendimento ao Cliente"
                          data-testid="course-title-input"
                          className="dialog-input"
                        />
                      </div>

                      {/* Thumbnail Upload Zone */}
                      <div className="form-group">
                        <Label className="form-label flex items-center gap-1">
                          <ImageIcon className="w-3.5 h-3.5" /> Imagem de Capa
                        </Label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleImageUpload(file);
                          }}
                          className="hidden"
                          id="course-thumbnail-input"
                          data-testid="course-thumbnail-input"
                        />
                        {courseForm.thumbnail_url ? (
                          <div className="relative rounded-xl overflow-hidden border-2 border-green-200 mt-1">
                            <img
                              src={courseForm.thumbnail_url}
                              alt="Preview da capa"
                              className="w-full h-36 object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => setCourseForm(prev => ({ ...prev, thumbnail_url: "" }))}
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Remover
                              </Button>
                            </div>
                            <span className="absolute bottom-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">✓ Imagem carregada</span>
                          </div>
                        ) : (
                          <label
                            htmlFor="course-thumbnail-input"
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '5.5rem', border: '1.5px dashed rgba(255,255,255,0.1)', borderRadius: 9, background: 'rgba(255,255,255,0.02)', cursor: 'pointer', marginTop: '0.25rem', transition: 'all 0.15s ease' }}
                          >
                            {imageUploading ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', color: '#6a6a9a' }}>
                                <div className="loading-spinner w-5 h-5" />
                                <span style={{ fontSize: '0.75rem' }}>Enviando imagem...</span>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', color: '#4a4a6a' }}>
                                <Upload size={20} />
                                <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#6a6a9a' }}>Clique para selecionar imagem</span>
                                <span style={{ fontSize: '0.67rem', color: '#3a3a5a' }}>JPG, PNG, WebP — 400×200px recomendado</span>
                              </div>
                            )}
                          </label>
                        )}
                      </div>

                      <div className="form-group">
                        <Label htmlFor="course-description" className="form-label">Descrição *</Label>
                        <Textarea
                          id="course-description"
                          value={courseForm.description}
                          onChange={(e) => setCourseForm(prev => ({ ...prev, description: e.target.value }))}
                          required
                          rows={3}
                          placeholder="Descreva os objetivos e conteúdo do curso..."
                          data-testid="course-description-input"
                          className="dialog-textarea"
                        />
                      </div>
                    </div>

                    {/* ── Seção 2: Acesso por Setores ── */}
                    <div className="space-y-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Lock size={10} style={{ color: '#a855f7' }} />
                        </div>
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Acesso por Setores *</span>
                      </div>

                      <div className="form-group">
                        {sectors.length === 0 ? (
                          <div style={{ padding: '0.75rem', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                            <p style={{ fontSize: '0.78rem', color: '#4a4a6a', margin: 0 }}>Carregando setores...</p>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {sectors.map(sector => {
                              const isChecked = courseForm.sectors.includes(sector.name);
                              return (
                                <label
                                  key={sector.name}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                    padding: '0.25rem 0.65rem', borderRadius: 20, cursor: 'pointer',
                                    fontSize: '0.75rem', fontWeight: 600, userSelect: 'none',
                                    fontFamily: "'Inter', sans-serif", transition: 'all 0.12s ease',
                                    background: isChecked ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.04)',
                                    border: isChecked ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.09)',
                                    color: isChecked ? '#c084fc' : '#6a6a9a',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setCourseForm(prev => ({ ...prev, sectors: [...prev.sectors, sector.name] }));
                                      } else {
                                        setCourseForm(prev => ({ ...prev, sectors: prev.sectors.filter(s => s !== sector.name) }));
                                      }
                                    }}
                                    data-testid={`sector-${sector.name}-checkbox`}
                                    className="hidden"
                                  />
                                  {isChecked && <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />}
                                  {sector.display_name}
                                </label>
                              );
                            })}
                          </div>
                        )}
                        {courseForm.sectors.length === 0 && sectors.length > 0 && (
                          <p style={{ fontSize: '0.72rem', color: '#f59e0b', marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>⚠ Selecione pelo menos um setor para prosseguir</p>
                        )}
                      </div>
                    </div>
                    
                    {/* ── Seção 3: Avaliação ── */}
                    <div className="space-y-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Award size={10} style={{ color: '#f59e0b' }} />
                        </div>
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Avaliação</span>
                      </div>

                      <div style={{ borderRadius: 9, border: courseForm.requires_assessment ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(255,255,255,0.07)', padding: '0.75rem', background: courseForm.requires_assessment ? 'rgba(245,158,11,0.05)' : 'rgba(255,255,255,0.03)', transition: 'all 0.15s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
                          <label htmlFor="requires-assessment" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              id="requires-assessment"
                              checked={courseForm.requires_assessment}
                              onChange={(e) => setCourseForm(prev => ({ ...prev, requires_assessment: e.target.checked }))}
                              className="w-4 h-4 accent-amber-500"
                              data-testid="requires-assessment-checkbox"
                            />
                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif" }}>Ativar Avaliação para este curso</span>
                          </label>
                          {courseForm.requires_assessment && (
                            <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)', padding: '1px 7px', borderRadius: 4 }}>Ativo</span>
                          )}
                        </div>

                        <div className={`grid grid-cols-2 gap-4 transition-opacity duration-200 ${!courseForm.requires_assessment ? 'opacity-40 pointer-events-none' : ''}`}>
                          <div className="form-group">
                            <Label htmlFor="course-ai-num-questions" className="form-label text-sm flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5" /> Nº de Questões
                            </Label>
                            <Input
                              id="course-ai-num-questions"
                              type="number"
                              min="1"
                              max="25"
                              value={courseAssessmentAIConfig.num_questions}
                              onChange={(e) => setCourseAssessmentAIConfig({ num_questions: e.target.value })}
                              disabled={!courseForm.requires_assessment}
                              className="dialog-input"
                            />
                          </div>
                          <div className="form-group">
                            <Label htmlFor="passing-score" className="form-label">
                              Nota Mínima (%)
                            </Label>
                            <Input
                              id="passing-score"
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={courseForm.passing_score_required}
                              onChange={(e) => setCourseForm(prev => ({ ...prev, passing_score_required: parseFloat(e.target.value) || 70.0 }))}
                              disabled={!courseForm.requires_assessment}
                              data-testid="passing-score-input"
                              className="dialog-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Seção 4: Módulos do Curso ── */}
                    <div className="space-y-3">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Layers size={10} style={{ color: '#4ade80' }} />
                          </div>
                          <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Módulos do Curso</span>
                        </div>
                        {courseForm.segments.length > 0 && (
                          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#4ade80', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', padding: '1px 7px', borderRadius: 4, fontFamily: 'monospace' }}>
                            {courseForm.segments.length} módulo{courseForm.segments.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      <DragDropContext onDragEnd={onDragEnd}>
                        <StrictModeDroppable droppableId="segments" direction="vertical">
                          {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-1.5">
                              {courseForm.segments.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '1.25rem', color: '#3a3a5a', fontSize: '0.78rem', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 9, background: 'rgba(255,255,255,0.02)' }}>
                                  Nenhum módulo adicionado ainda — use o formulário abaixo
                                </div>
                              )}
                              {courseForm.segments.map((segment, index) => (
                                <Draggable key={segment.id} draggableId={String(segment.id)} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                        display: 'flex', alignItems: 'center', gap: '0.6rem',
                                        padding: '0.5rem 0.65rem', borderRadius: 8,
                                        background: snapshot.isDragging ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.04)',
                                        border: snapshot.isDragging ? '1px solid rgba(59,130,246,0.25)' : '1px solid rgba(255,255,255,0.07)',
                                        transition: 'background 0.1s ease',
                                      }}
                                    >
                                      <div {...provided.dragHandleProps} style={{ color: '#3a3a5a', cursor: 'grab', flexShrink: 0 }}>
                                        <GripVertical size={14} />
                                      </div>
                                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', color: '#7a7a9a', fontSize: '0.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'monospace' }}>
                                        {index + 1}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem' }}>
                                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif" }}>{segment.title}</span>
                                          {(() => {
                                            const status = segmentTranscriptionStatus[segment.id] ?? (segment.transcription_text ? 'completed' : 'checking');
                                            const badge = getTranscriptionBadgeConfig(status);
                                            return (
                                              <span className={`badge-soft text-[0.65rem] ${badge.className}`}>
                                                {badge.label}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.1rem' }}>
                                          <Clock size={10} style={{ color: '#3a3a5a' }} />
                                          <span style={{ fontSize: '0.7rem', color: '#4a4a6a' }}>{segment.duration_minutes} min</span>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeSegment(index)}
                                        style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', color: '#f87171', cursor: 'pointer' }}
                                        data-testid={`remove-segment-${index}`}
                                      >
                                        <Trash2 size={11} />
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </StrictModeDroppable>
                      </DragDropContext>

                      {/* Add new segment */}
                      <div style={{ borderRadius: 9, border: '1px dashed rgba(34,197,94,0.2)', padding: '0.75rem', background: 'rgba(34,197,94,0.03)', marginTop: '0.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.6rem' }}>
                          <Plus size={12} style={{ color: '#4ade80' }} />
                          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#4ade80', fontFamily: "'Inter', sans-serif" }}>Adicionar Novo Módulo</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            placeholder="Título do módulo"
                            value={newSegment.title}
                            onChange={(e) => setNewSegment(prev => ({ ...prev, title: e.target.value }))}
                            data-testid="segment-title-input"
                            className="dialog-input"
                          />
                          <div className="relative">
                            <Input
                              type="number"
                              placeholder="Duração (min)"
                              value={newSegment.duration_minutes}
                              onChange={(e) => setNewSegment(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                              data-testid="segment-duration-input"
                              className="dialog-input pr-10"
                            />
                            <Clock size={13} style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#3a3a5a', pointerEvents: 'none' }} />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Inter', sans-serif" }}>Vídeo do módulo</label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              onClick={() => setShowVideoUploadManager(true)}
                              size="sm"
                              style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.7rem', borderRadius: 7, fontSize: '0.75rem', fontWeight: 600, background: newSegment.video_id ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)', border: newSegment.video_id ? '1px solid rgba(34,197,94,0.25)' : '1px solid rgba(255,255,255,0.1)', color: newSegment.video_id ? '#4ade80' : '#7a7a9a', cursor: 'pointer' }}
                            >
                              <Video size={12} />
                              {newSegment.video_id ? 'Vídeo Enviado ✓' : 'Upload de Vídeo'}
                            </Button>
                            <div className="relative flex-1">
                              <Globe size={12} style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#3a3a5a', pointerEvents: 'none' }} />
                              <Input
                                placeholder="Ou cole a URL do YouTube"
                                value={newSegment.video_url}
                                onChange={(e) => {
                                  setNewSegment(prev => ({
                                    ...prev,
                                    video_url: e.target.value,
                                    video_type: e.target.value ? "youtube" : "local"
                                  }));
                                }}
                                data-testid="segment-url-input"
                                className="dialog-input pl-9"
                              />
                            </div>
                          </div>
                          {newSegment.video_id && (
                            <p style={{ fontSize: '0.7rem', color: '#4ade80', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <CheckCircle2 size={11} /> Vídeo selecionado (ID: {newSegment.video_id.substring(0, 8)}...)
                            </p>
                          )}
                        </div>

                        <Textarea
                          placeholder="Descrição do módulo (opcional)"
                          value={newSegment.description}
                          onChange={(e) => setNewSegment(prev => ({ ...prev, description: e.target.value }))}
                          rows={2}
                          data-testid="segment-description-input"
                          className="dialog-textarea"
                        />
                        <button
                          type="button"
                          onClick={addSegment}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.45rem', borderRadius: 7, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.2)', color: '#4ade80', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                          data-testid="add-segment-btn"
                        >
                          <Plus size={13} />
                          Adicionar Módulo
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '0.25rem' }}>
                      <Button
                        type="button"
                        onClick={() => { setCourseDialogOpen(false); resetCourseForm(); }}
                        data-testid="cancel-course-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        data-testid="save-course-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(231,126,34,0.18)', border: '1px solid rgba(231,126,34,0.3)', color: '#f39c12', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                      >
                        {editingCourse ? <><Save size={13} /> Atualizar Curso</> : <><Plus size={13} /> Criar Curso</>}
                      </Button>
                    </div>
                  </form>
              </DialogContent>
                </Dialog>

                <Button
                  variant="outline"
                  onClick={() => setCreateTrailSignal(s => s + 1)}
                  data-testid="create-trail-top-btn"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                    padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#8a8aaa',
                    fontSize: '0.79rem', fontWeight: 500, cursor: 'pointer',
                    fontFamily: "'Inter', sans-serif",
                    letterSpacing: '0.01em',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Route size={14} />
                  Nova Trilha
                </Button>
          </div>
          </div>
          
            {/* Unified Trails Manager */}
            <div id="trails-section">
              <TrailsManager
                  token={token}
                  sectors={sectors}
                  courses={courses}
                  assessments={assessments}
                  fetchAdminData={fetchAdminData}
                  onEditCourse={handleEditCourse}
                  onDeleteCourse={handleDeleteCourse}
                  onNewCourse={() => {
                    resetCourseForm();
                    setCourseDialogOpen(true);
                  }}
                  openCreateTrailSignal={createTrailSignal}
                />
            </div>
          </TabsContent>

          <TabsContent value="approvals" className="space-y-6 mt-6">
            {/* User Approvals */}
            <div>
              <h2 className="heading-secondary mb-4">Aprovação de Usuários</h2>
              <p className="text-body mb-6">Aprove ou rejeite novos usuários cadastrados na plataforma</p>
              
              {pendingUsers.length === 0 ? (
                <Card className="card text-center py-12">
                  <CardContent>
                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="heading-tertiary text-gray-600 mb-2">
                      Nenhuma aprovação pendente
                    </h3>
                    <p className="text-body text-gray-500">
                      Todos os usuários foram processados.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {pendingUsers.map((pendingUser) => (
                    <Card key={pendingUser.id} className="card">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="heading-tertiary">{pendingUser.name}</h3>
                            <p className="text-body text-gray-600">{pendingUser.email}</p>
                            
                            <div className="flex items-center gap-4 mt-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Setor Solicitado:</span>
                                <Badge variant="outline">
                                  {getSectorDisplayName(pendingUser.sector)}
                                </Badge>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Função:</span>
                                <Badge variant="secondary">
                                  {getRoleDisplayName(pendingUser.role)}
                                </Badge>
                              </div>
                              
                              <span className="text-sm text-gray-500">
                                Cadastrado em: {new Date(pendingUser.created_at).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {/* Sector selection for approval */}
                            <Select 
                              defaultValue={pendingUser.sector}
                              onValueChange={(value) => {
                                pendingUser.selectedSector = value;
                              }}
                              data-testid={`sector-select-${pendingUser.id}`}
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue placeholder="Setor" />
                              </SelectTrigger>
                              <SelectContent>
                                {sectors.map(sector => (
                                  <SelectItem key={sector.name} value={sector.name}>
                                    {sector.display_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            <Button
                              onClick={() => handleUserApproval(pendingUser.id, true, pendingUser.selectedSector || pendingUser.sector)}
                              className="btn btn-success"
                              data-testid={`approve-user-${pendingUser.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Aprovar
                            </Button>
                            
                            <Button
                              onClick={() => handleUserApproval(pendingUser.id, false)}
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`reject-user-${pendingUser.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Rejeitar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="assessments" className="mt-0">
            {/* Assessments Management Header */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              gap: '1rem', flexWrap: 'wrap', paddingTop: '1.5rem', marginBottom: '1.25rem',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.2rem' }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: 'rgba(168,85,247,0.1)',
                    border: '1px solid rgba(168,85,247,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Target size={14} style={{ color: '#a855f7' }} />
                  </div>
                  <h2 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '1.08rem', fontWeight: 700,
                    color: '#e8e8f0', margin: 0, letterSpacing: '-0.01em',
                  }}>
                    Avaliações
                  </h2>
                  {assessments.length > 0 && (
                    <span style={{
                      fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 600,
                      color: '#a855f7', background: 'rgba(168,85,247,0.1)',
                      border: '1px solid rgba(168,85,247,0.2)',
                      padding: '1px 7px', borderRadius: 5,
                    }}>
                      {assessments.length}
                    </span>
                  )}
                </div>
                <p style={{
                  fontSize: '0.77rem', color: '#4a4a6a',
                  fontFamily: "'Inter', sans-serif", margin: 0, paddingLeft: '2.55rem',
                }}>
                  Avaliações vinculadas a cursos e trilhas de aprendizado
                </p>
              </div>

              <Dialog open={assessmentDialogOpen} onOpenChange={setAssessmentDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="create-assessment-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                      padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto',
                      background: 'rgba(168,85,247,0.15)',
                      border: '1px solid rgba(168,85,247,0.25)',
                      color: '#c084fc',
                      fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                      letterSpacing: '0.01em',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Plus size={14} />
                    Nova Avaliação
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
                  <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
                    <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
                      <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Target size={13} style={{ color: '#a855f7' }} />
                      </div>
                      {editingAssessment ? 'Editar Avaliação' : 'Nova Avaliação'}
                    </DialogTitle>
                    <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
                      Configure as informações básicas e adicione questões
                    </DialogDescription>
                  </DialogHeader>

                  <form onSubmit={handleCreateAssessment} className="space-y-5">
                    {/* Assessment Basic Info */}
                    <div className="space-y-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Target size={10} style={{ color: '#a855f7' }} />
                        </div>
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Configurações</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="form-group">
                          <Label htmlFor="assessment-title" className="form-label">
                            Título da Avaliação *
                          </Label>
                          <Input
                            id="assessment-title"
                            value={assessmentForm.title}
                            onChange={(e) => setAssessmentForm(prev => ({ ...prev, title: e.target.value }))}
                            required
                            data-testid="assessment-title-input"
                            className="dialog-input"
                            placeholder="Digite o título da avaliação"
                          />
                        </div>
                        
                        <div className="form-group">
                          <Label htmlFor="assessment-course" className="form-label">
                            Curso Vinculado
                          </Label>
                          <Select
                            value={assessmentForm.course_id || "no-course"}
                            onValueChange={(value) => setAssessmentForm(prev => ({
                              ...prev,
                              course_id: value === "no-course" ? "" : value
                            }))}
                            data-testid="assessment-course-select"
                          >
                            <SelectTrigger className="form-input">
                              <SelectValue placeholder="Selecione um curso (opcional)" />
                            </SelectTrigger>
                            <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                              <SelectItem value="no-course">Nenhum curso</SelectItem>
                              {courses.map(course => (
                                <SelectItem key={course.id} value={course.id}>
                                  {course.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="form-group">
                        <Label htmlFor="assessment-description" className="form-label">
                          Descrição *
                        </Label>
                        <Textarea
                          id="assessment-description"
                          value={assessmentForm.description}
                          onChange={(e) => setAssessmentForm(prev => ({ ...prev, description: e.target.value }))}
                          required
                          rows={3}
                          data-testid="assessment-description-input"
                          className="dialog-textarea"
                          placeholder="Descreva o objetivo e conteúdo da avaliação"
                        />
                      </div>

                      <div className="grid grid-cols-4 gap-3">
                        <div className="form-group">
                          <Label htmlFor="time-limit" className="form-label">
                            Tempo (min)
                          </Label>
                          <Input
                            id="time-limit"
                            type="number"
                            min="0"
                            value={assessmentForm.time_limit}
                            onChange={(e) => setAssessmentForm(prev => ({ ...prev, time_limit: parseInt(e.target.value) || 0 }))}
                            data-testid="time-limit-input"
                            className="dialog-input"
                          />
                        </div>

                        <div className="form-group">
                          <Label htmlFor="passing-grade" className="form-label">
                            Nota Mín. (%)
                          </Label>
                          <Input
                            id="passing-grade"
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={assessmentForm.passing_grade}
                            onChange={(e) => setAssessmentForm(prev => ({ ...prev, passing_grade: parseFloat(e.target.value) || 0 }))}
                            data-testid="passing-grade-input"
                            className="dialog-input"
                          />
                        </div>

                        <div className="form-group">
                          <Label htmlFor="max-attempts" className="form-label">
                            Tentativas
                          </Label>
                          <Input
                            id="max-attempts"
                            type="number"
                            min="1"
                            value={assessmentForm.max_attempts}
                            onChange={(e) => setAssessmentForm(prev => ({ ...prev, max_attempts: parseInt(e.target.value) || 1 }))}
                            data-testid="max-attempts-input"
                            className="dialog-input"
                          />
                        </div>

                        <div className="form-group">
                          <Label htmlFor="cooldown-period" className="form-label">
                            Cooldown (h)
                          </Label>
                          <Input
                            id="cooldown-period"
                            type="number"
                            min="0"
                            value={assessmentForm.cooldown_period}
                            onChange={(e) => setAssessmentForm(prev => ({ ...prev, cooldown_period: parseInt(e.target.value) || 0 }))}
                            data-testid="cooldown-period-input"
                            className="dialog-input"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <Label className="form-label">Setores com Acesso</Label>
                        {sectors.length === 0 ? (
                          <div style={{ padding: '0.65rem', borderRadius: 7, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                            <p style={{ fontSize: '0.78rem', color: '#4a4a6a', margin: 0 }}>Carregando setores...</p>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '0.4rem', marginTop: '0.35rem', maxHeight: '7rem', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '0.5rem', background: 'rgba(255,255,255,0.02)' }}>
                            {sectors.map(sector => {
                              const isChecked = assessmentForm.sectors.includes(sector.name);
                              return (
                                <label key={sector.name} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.4rem', borderRadius: 6, cursor: 'pointer', background: isChecked ? 'rgba(168,85,247,0.1)' : 'transparent', border: isChecked ? '1px solid rgba(168,85,247,0.2)' : '1px solid transparent', transition: 'all 0.1s ease' }}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setAssessmentForm(prev => ({
                                          ...prev,
                                          sectors: [...prev.sectors, sector.name]
                                        }));
                                      } else {
                                        setAssessmentForm(prev => ({
                                          ...prev,
                                          sectors: prev.sectors.filter(s => s !== sector.name)
                                        }));
                                      }
                                    }}
                                    data-testid={`assessment-sector-${sector.name}-checkbox`}
                                    className="w-3 h-3 accent-purple-500"
                                  />
                                  <span style={{ fontSize: '0.73rem', fontWeight: 500, color: isChecked ? '#c084fc' : '#6a6a9a', fontFamily: "'Inter', sans-serif" }}>{sector.display_name}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Questions Section */}
                    <div className="space-y-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FileText size={10} style={{ color: '#60a5fa' }} />
                        </div>
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Questões</span>
                      </div>

                      <Tabs value={activeAssessmentTab} onValueChange={setActiveAssessmentTab} className="w-full">
                        <TabsList style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '2px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)' }}>
                          <TabsTrigger value="manual" data-testid="manual-tab" style={{ borderRadius: 6, fontSize: '0.75rem' }}>
                            <FileText size={13} style={{ marginRight: '0.35rem' }} />
                            Manual
                          </TabsTrigger>
                          <TabsTrigger value="ai" data-testid="ai-tab" style={{ borderRadius: 6, fontSize: '0.75rem' }}>
                            <Brain size={13} style={{ marginRight: '0.35rem' }} />
                            IA
                          </TabsTrigger>
                          <TabsTrigger value="text" data-testid="text-tab" style={{ borderRadius: 6, fontSize: '0.75rem' }}>
                            <FileText size={13} style={{ marginRight: '0.35rem' }} />
                            Texto
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="ai" className="mt-3">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem', borderRadius: 9, border: '1px solid rgba(168,85,247,0.15)', background: 'rgba(168,85,247,0.04)' }}>
                            <div className="space-y-1.5">
                              <Label htmlFor="url" className="form-label">URL do Conteúdo (opcional)</Label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <Globe size={13} style={{ color: '#4a4a6a', flexShrink: 0 }} />
                                <Input
                                  id="url"
                                  value={aiGenerationData.url}
                                  onChange={(e) => setAiGenerationData({
                                    ...aiGenerationData,
                                    url: e.target.value
                                  })}
                                  placeholder="https://example.com/article"
                                  data-testid="url-input"
                                  className="dialog-input"
                                />
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <Label htmlFor="ai_description" className="form-label">Descrição detalhada (opcional)</Label>
                              <Textarea
                                id="ai_description"
                                value={aiGenerationData.description}
                                onChange={(e) => setAiGenerationData({
                                  ...aiGenerationData,
                                  description: e.target.value
                                })}
                                placeholder="Conte o que a avaliação deve abordar caso não tenha um link..."
                                rows={4}
                                data-testid="ai-description-input"
                                className="dialog-textarea"
                              />
                              <p style={{ fontSize: '0.7rem', color: '#4a4a6a', margin: 0 }}>Preencha pelo menos um dos campos acima.</p>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <Label htmlFor="ai_title" className="form-label">Título da Avaliação</Label>
                                <Input
                                  id="ai_title"
                                  value={aiGenerationData.assessment_title}
                                  onChange={(e) => setAiGenerationData({
                                    ...aiGenerationData,
                                    assessment_title: e.target.value
                                  })}
                                  placeholder="Ex: Avaliação de JavaScript"
                                  data-testid="ai-title-input"
                                  className="dialog-input"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor="num_questions" className="form-label">Nº Questões</Label>
                                <Input
                                  id="num_questions"
                                  type="number"
                                  min="1"
                                  max="20"
                                  value={aiGenerationData.num_questions}
                                  onChange={(e) => setAiGenerationData({
                                    ...aiGenerationData,
                                    num_questions: parseInt(e.target.value) || 10
                                  })}
                                  data-testid="num-questions-input"
                                  className="dialog-input"
                                />
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={generateQuestionsFromAI}
                              disabled={generatingQuestions}
                              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem', borderRadius: 7, background: 'rgba(168,85,247,0.18)', border: '1px solid rgba(168,85,247,0.3)', color: '#c084fc', fontSize: '0.8rem', fontWeight: 600, cursor: generatingQuestions ? 'not-allowed' : 'pointer', fontFamily: "'Inter', sans-serif", opacity: generatingQuestions ? 0.6 : 1 }}
                              data-testid="generate-questions-btn"
                            >
                              {generatingQuestions ? (
                                <><Loader2 size={14} className="animate-spin" /> Gerando questões...</>
                              ) : (
                                <><Brain size={14} /> Gerar Questões com IA</>
                              )}
                            </button>

                            {hasAiGenerated && (
                              <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, background: 'rgba(255,255,255,0.03)', padding: '0.65rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
                                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif" }}>Questões geradas</span>
                                  <button
                                    type="button"
                                    onClick={generateAdditionalAIQuestion}
                                    disabled={generatingQuestions}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.25rem 0.6rem', borderRadius: 6, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', color: '#c084fc', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}
                                  >
                                    {generatingQuestions ? <><Loader2 size={11} className="animate-spin" /> Gerando...</> : <><Plus size={11} /> + Pergunta</>}
                                  </button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '16rem', overflowY: 'auto' }}>
                                  {questions.map((question, index) => (
                                    <div key={`ai-question-${index}`} style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 7, padding: '0.55rem 0.65rem', background: 'rgba(255,255,255,0.03)' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6a6a9a', fontFamily: 'monospace' }}>Q{index + 1}</span>
                                        <button
                                          type="button"
                                          onClick={() => removeQuestion(index)}
                                          style={{ width: 22, height: 22, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', color: '#f87171', cursor: 'pointer' }}
                                        >
                                          <Trash2 size={10} />
                                        </button>
                                      </div>
                                      <p style={{ fontSize: '0.78rem', fontWeight: 600, color: '#c0c0d8', marginBottom: '0.3rem', fontFamily: "'Inter', sans-serif" }}>{question.question_text}</p>
                                      <ul style={{ fontSize: '0.73rem', color: '#6a6a9a', paddingLeft: '0.75rem', margin: 0 }}>
                                        {question.options.map((option, optIndex) => (
                                          <li key={optIndex}>{option}</li>
                                        ))}
                                      </ul>
                                      <p style={{ fontSize: '0.67rem', color: '#4ade80', marginTop: '0.25rem', fontFamily: 'monospace' }}>✓ {question.correct_answer}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </TabsContent>
                        <TabsContent value="text" className="mt-3">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem', borderRadius: 9, border: '1px solid rgba(34,197,94,0.15)', background: 'rgba(34,197,94,0.04)' }}>
                            <div className="space-y-1.5">
                              <Label htmlFor="text_title" className="form-label">Título da Avaliação</Label>
                              <Input
                                id="text_title"
                                value={textImportData.assessment_title}
                                onChange={(e) => setTextImportData({
                                  ...textImportData,
                                  assessment_title: e.target.value
                                })}
                                placeholder="Ex: Avaliação de História do Brasil"
                                data-testid="text-title-input"
                                className="dialog-input"
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label htmlFor="questions_text" className="form-label">Texto com Questões</Label>
                              <Textarea
                                id="questions_text"
                                value={textImportData.text}
                                onChange={(e) => setTextImportData({
                                  ...textImportData,
                                  text: e.target.value
                                })}
                                placeholder="Cole suas questões aqui..."
                                rows={8}
                                className="dialog-textarea font-mono text-sm"
                                data-testid="questions-text-input"
                              />
                            </div>

                            <button
                              type="button"
                              onClick={parseTextQuestions}
                              disabled={generatingQuestions}
                              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem', borderRadius: 7, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.25)', color: '#4ade80', fontSize: '0.8rem', fontWeight: 600, cursor: generatingQuestions ? 'not-allowed' : 'pointer', fontFamily: "'Inter', sans-serif", opacity: generatingQuestions ? 0.6 : 1 }}
                              data-testid="parse-text-btn"
                            >
                              {generatingQuestions ? (
                                <><Loader2 size={14} className="animate-spin" /> Processando texto...</>
                              ) : (
                                <><FileText size={14} /> Processar Texto</>
                              )}
                            </button>
                          </div>
                        </TabsContent>

                        <TabsContent value="manual" className="mt-3">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '22rem', overflowY: 'auto' }}>
                            {questions.map((question, questionIndex) => (
                              <div key={questionIndex} style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9, padding: '0.65rem 0.75rem', background: 'rgba(255,255,255,0.03)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6a6a9a', fontFamily: 'monospace' }}>Questão {questionIndex + 1}</span>
                                  {questions.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeQuestion(questionIndex)}
                                      style={{ width: 22, height: 22, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', color: '#f87171', cursor: 'pointer' }}
                                      data-testid={`remove-question-${questionIndex}`}
                                    >
                                      <Minus size={10} />
                                    </button>
                                  )}
                                </div>

                                <div className="space-y-3">
                                  <div className="space-y-1">
                                    <Label className="form-label">Texto da Pergunta</Label>
                                    <Textarea
                                      value={question.question_text}
                                      onChange={(e) => handleQuestionChange(questionIndex, 'question_text', e.target.value)}
                                      placeholder="Digite a pergunta..."
                                      data-testid={`question-text-${questionIndex}`}
                                      className="dialog-textarea"
                                    />
                                  </div>

                                  <div className="space-y-1">
                                    <Label className="form-label">Opções de Resposta</Label>
                                    <div className="space-y-1.5">
                                      {question.options.map((option, optionIndex) => (
                                        <Input
                                          key={optionIndex}
                                          value={option}
                                          onChange={(e) => handleOptionChange(questionIndex, optionIndex, e.target.value)}
                                          placeholder={`Opção ${String.fromCharCode(65 + optionIndex)}`}
                                          data-testid={`option-${questionIndex}-${optionIndex}`}
                                          className="dialog-input"
                                        />
                                      ))}
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                      <Label className="form-label">Resposta Correta</Label>
                                      <Select
                                        value={question.correct_answer}
                                        onValueChange={(value) => handleQuestionChange(questionIndex, 'correct_answer', value)}
                                        data-testid={`correct-answer-${questionIndex}`}
                                      >
                                        <SelectTrigger className="form-input">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                          <SelectItem value="A">A</SelectItem>
                                          <SelectItem value="B">B</SelectItem>
                                          <SelectItem value="C">C</SelectItem>
                                          <SelectItem value="D">D</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="form-label">Pontuação</Label>
                                      <Input
                                        type="number"
                                        min="0.1"
                                        step="0.1"
                                        value={question.points}
                                        onChange={(e) => handleQuestionChange(questionIndex, 'points', parseFloat(e.target.value) || 1)}
                                        data-testid={`points-${questionIndex}`}
                                        className="dialog-input"
                                      />
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <Label>Explicação (opcional)</Label>
                                    <Textarea
                                      value={question.explanation}
                                      onChange={(e) => handleQuestionChange(questionIndex, 'explanation', e.target.value)}
                                      placeholder="Explicação da resposta correta"
                                      rows={2}
                                      data-testid={`explanation-${questionIndex}`}
                                      className="dialog-textarea"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={addQuestion}
                              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem', borderRadius: 7, background: 'transparent', border: '1px dashed rgba(255,255,255,0.1)', color: '#5a5a8a', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif' " }}
                              data-testid="add-question-btn"
                            >
                              <Plus size={13} />
                              Adicionar Questão
                            </button>
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '0.25rem' }}>
                      <Button
                        type="button"
                        onClick={() => { setAssessmentDialogOpen(false); resetAssessmentForm(); }}
                        data-testid="cancel-assessment-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        data-testid="save-assessment-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(168,85,247,0.18)', border: '1px solid rgba(168,85,247,0.3)', color: '#c084fc', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                      >
                        {editingAssessment ? 'Atualizar' : 'Criar'} Avaliação
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {/* Assessments List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {assessments.map(assessment => {
                const linkedTrails = assessment.linked_trails || [];
                const linkedCourse = assessment.course_id
                  ? courses.find(c => c.id === assessment.course_id)
                  : null;
                return (
                  <div
                    key={assessment.id}
                    style={{
                      background: '#12121a',
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: 11,
                      padding: '0.75rem 0.875rem',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                    }}
                  >
                    {/* Icon badge */}
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0, marginTop: '0.05rem',
                      background: 'rgba(168,85,247,0.1)',
                      border: '1px solid rgba(168,85,247,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Target size={14} style={{ color: '#a855f7' }} />
                    </div>

                    {/* Body */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                        <span style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: '0.88rem', fontWeight: 700, color: '#e0e0f0',
                        }}>
                          {assessment.title}
                        </span>
                        {linkedCourse && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            padding: '1px 6px', borderRadius: 4,
                            background: 'rgba(34,197,94,0.08)',
                            border: '1px solid rgba(34,197,94,0.15)',
                            fontSize: '0.65rem', fontWeight: 600, color: '#4ade80',
                            fontFamily: "'Inter', sans-serif",
                          }}>
                            <BookOpen size={9} />
                            {linkedCourse.title}
                          </span>
                        )}
                        {linkedTrails.length > 0 && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            padding: '1px 6px', borderRadius: 4,
                            background: 'rgba(99,102,241,0.08)',
                            border: '1px solid rgba(99,102,241,0.15)',
                            fontSize: '0.65rem', fontWeight: 600, color: '#818cf8',
                            fontFamily: "'Inter', sans-serif",
                          }}>
                            <Route size={9} />
                            {linkedTrails.length} trilha{linkedTrails.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      {assessment.description && (
                        <p style={{
                          fontSize: '0.76rem', color: '#4a4a6a',
                          fontFamily: "'Inter', sans-serif", margin: '0 0 0.45rem',
                          lineHeight: 1.5,
                          overflow: 'hidden', display: '-webkit-box',
                          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                          {assessment.description}
                        </p>
                      )}

                      {/* Meta row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {[
                          { label: `${assessment.question_count || 0} questões`, color: '#4a4a6a' },
                          { label: `${assessment.passing_grade}% mín.`, color: '#4a4a6a' },
                          { label: `${assessment.max_attempts} tentativa${assessment.max_attempts !== 1 ? 's' : ''}`, color: '#4a4a6a' },
                          ...(assessment.time_limit ? [{ label: `${assessment.time_limit} min`, color: '#4a4a6a' }] : []),
                        ].map((chip, i) => (
                          <span key={i} style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: '1px 7px', borderRadius: 5,
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.07)',
                            fontSize: '0.68rem', color: chip.color,
                            fontFamily: 'monospace',
                          }}>
                            {chip.label}
                          </span>
                        ))}

                        {/* Sector badges */}
                        {(assessment.sectors || []).map(sector => (
                          <span key={sector} style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: '1px 6px', borderRadius: 4,
                            background: 'rgba(230,126,34,0.08)',
                            border: '1px solid rgba(230,126,34,0.15)',
                            fontSize: '0.65rem', fontWeight: 600, color: '#fb923c',
                            fontFamily: "'Inter', sans-serif",
                          }}>
                            {getSectorDisplayName(sector)}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                      <button data-testid={`view-assessment-${assessment.id}`} title="Visualizar" className="admin-icon-btn">
                        <Eye size={12} />
                      </button>
                      <button onClick={() => handleEditAssessment(assessment)} data-testid={`edit-assessment-${assessment.id}`} title="Editar" className="admin-icon-btn edit">
                        <Edit size={12} />
                      </button>
                      <button onClick={() => handleDeleteAssessment(assessment.id, assessment.title)} data-testid={`delete-assessment-${assessment.id}`} title="Excluir" className="admin-icon-btn danger delete">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {assessments.length === 0 && (
                <div style={{
                  background: '#12121a',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 12, padding: '2.5rem 1.5rem', textAlign: 'center',
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 11,
                    background: 'rgba(168,85,247,0.07)',
                    border: '1px solid rgba(168,85,247,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 0.85rem',
                  }}>
                    <Target size={20} style={{ color: '#5a3a7a' }} />
                  </div>
                  <h3 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '0.92rem', fontWeight: 600, color: '#6a6a8a', marginBottom: '0.3rem',
                  }}>
                    Nenhuma avaliação criada
                  </h3>
                  <p style={{
                    fontSize: '0.77rem', color: '#3a3a5a', marginBottom: '1.1rem',
                    fontFamily: "'Inter', sans-serif",
                  }}>
                    Comece criando sua primeira avaliação para os cursos.
                  </p>
                  <button
                    onClick={() => setAssessmentDialogOpen(true)}
                    data-testid="create-first-assessment-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.45rem 1rem', borderRadius: 8,
                      background: 'rgba(168,85,247,0.15)',
                      border: '1px solid rgba(168,85,247,0.25)',
                      color: '#c084fc',
                      fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    <Plus size={13} />
                    Criar Primeira Avaliação
                  </button>
                </div>
              )}
            </div>
          </TabsContent>



          

          <TabsContent value="users" className="mt-6">

            {/* ── Section header ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingTop: '1.5rem', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.2rem' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <GraduationCap size={14} style={{ color: '#818cf8' }} />
                  </div>
                  <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.08rem', fontWeight: 700, color: '#e8e8f0', margin: 0, letterSpacing: '-0.01em' }}>Alunos</h2>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 600, color: '#818cf8', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', padding: '1px 7px', borderRadius: 5 }}>
                    {users.filter(u => u.role === 'employee').length}
                  </span>
                </div>
                <p style={{ fontSize: '0.77rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", margin: 0, paddingLeft: '2.55rem' }}>
                  Colaboradores cadastrados na plataforma
                </p>
              </div>

              <Dialog
                open={userDialogOpen && !systemUserMode}
                onOpenChange={(isOpen) => {
                  if (!isOpen) { setSystemUserMode(false); resetUserForm(); }
                  setUserDialogOpen(isOpen);
                }}
              >
                <DialogTrigger asChild>
                  <button
                    onClick={() => { setSystemUserMode(false); resetUserForm(); }}
                    data-testid="create-user-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                      padding: '0.42rem 0.95rem', borderRadius: 8, cursor: 'pointer',
                      background: 'rgba(99,102,241,0.15)',
                      border: '1px solid rgba(99,102,241,0.28)',
                      color: '#818cf8',
                      fontSize: '0.79rem', fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                      letterSpacing: '0.01em',
                      transition: 'all 0.15s ease',
                      flexShrink: 0,
                    }}
                  >
                    <Plus size={14} />
                    Novo Aluno
                  </button>
                </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
                      <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
                        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
                          <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users size={13} style={{ color: '#818cf8' }} />
                          </div>
                          {editingUser
                            ? (systemUserMode ? 'Editar Usuário do Sistema' : 'Editar Aluno')
                            : (systemUserMode ? 'Novo Usuário do Sistema' : 'Novo Aluno')}
                        </DialogTitle>
                        <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
                          {editingUser
                            ? (systemUserMode ? 'Atualize os dados do usuário do sistema' : 'Atualize os dados do aluno')
                            : (systemUserMode ? 'Cadastre um administrador ou coordenador' : 'Adicione um novo aluno à plataforma')}
                        </DialogDescription>
                      </DialogHeader>

                      <form onSubmit={handleSaveUser} className="space-y-4">
                        <div className="space-y-3">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Users size={10} style={{ color: '#818cf8' }} />
                            </div>
                            <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Informações Básicas</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="form-group">
                              <Label htmlFor="user-name" className="form-label">Nome Completo *</Label>
                              <Input id="user-name" value={userForm.name} onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))} required data-testid="user-name-input" placeholder="Nome do colaborador" className="dialog-input" />
                            </div>
                            <div className="form-group">
                              <Label htmlFor="user-email" className="form-label">Email *</Label>
                              <Input id="user-email" type="email" value={userForm.email} onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))} required data-testid="user-email-input" placeholder="email@empresa.com" className="dialog-input" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="form-group">
                              <Label htmlFor="user-phone" className="form-label">Telefone</Label>
                              <Input id="user-phone" value={userForm.phone} onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))} data-testid="user-phone-input" placeholder="(00) 00000-0000" className="dialog-input" />
                            </div>
                            <div className="form-group">
                              <Label htmlFor="user-code" className="form-label">Matrícula</Label>
                              <Input id="user-code" value={userForm.code} onChange={(e) => setUserForm(prev => ({ ...prev, code: e.target.value }))} data-testid="user-code-input" placeholder="Número de matrícula" className="dialog-input" />
                            </div>
                          </div>
                          <div className="form-group">
                            <Label htmlFor="user-address" className="form-label">Endereço</Label>
                            <Input id="user-address" value={userForm.address} onChange={(e) => setUserForm(prev => ({ ...prev, address: e.target.value }))} data-testid="user-address-input" placeholder="Rua, número, bairro, cidade" className="dialog-input" />
                          </div>
                          <div className="form-group">
                            <Label htmlFor="user-password" className="form-label">
                              {editingUser ? 'Nova Senha (deixe em branco para não alterar)' : 'Senha Temporária *'}
                            </Label>
                            <Input id="user-password" type="password" value={userForm.password} onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))} required={!editingUser} data-testid="user-password-input" placeholder={editingUser ? "Digite a nova senha" : "Senha inicial"} className="dialog-input" />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <BookOpen size={10} style={{ color: '#818cf8' }} />
                            </div>
                            <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Informações Acadêmicas</span>
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="form-group">
                              <Label htmlFor="user-shift" className="form-label">Turno</Label>
                              <Select value={userForm.shift} onValueChange={(value) => setUserForm(prev => ({ ...prev, shift: value }))} data-testid="user-shift-select">
                                <SelectTrigger className="form-input"><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                  <SelectItem value="Matutino">Matutino</SelectItem>
                                  <SelectItem value="Vespertino">Vespertino</SelectItem>
                                  <SelectItem value="Noturno">Noturno</SelectItem>
                                  <SelectItem value="Integral">Integral</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="form-group">
                              <Label htmlFor="user-period" className="form-label">Período</Label>
                              <Input id="user-period" value={userForm.period} onChange={(e) => setUserForm(prev => ({ ...prev, period: e.target.value }))} data-testid="user-period-input" placeholder="Ex: 3º Semestre" className="dialog-input" />
                            </div>
                            <div className="form-group">
                              <Label htmlFor="user-modality" className="form-label">Modalidade</Label>
                              <Select value={userForm.modality} onValueChange={(value) => setUserForm(prev => ({ ...prev, modality: value }))} data-testid="user-modality-select">
                                <SelectTrigger className="form-input"><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                  <SelectItem value="Presencial">Presencial</SelectItem>
                                  <SelectItem value="EAD">EAD</SelectItem>
                                  <SelectItem value="Híbrido">Híbrido</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="form-group">
                              <Label htmlFor="user-start-date" className="form-label">Data de Ingresso</Label>
                              <Input id="user-start-date" value={userForm.start_date} onChange={(e) => setUserForm(prev => ({ ...prev, start_date: e.target.value }))} data-testid="user-start-date-input" placeholder="DD/MM/AAAA" className="dialog-input" />
                            </div>
                            <div className="form-group">
                              <Label htmlFor="user-status" className="form-label">Situação</Label>
                              <Select value={userForm.status} onValueChange={(value) => setUserForm(prev => ({ ...prev, status: value }))} data-testid="user-status-select">
                                <SelectTrigger className="form-input"><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                  <SelectItem value="Ativo">Ativo</SelectItem>
                                  <SelectItem value="Inativo">Inativo</SelectItem>
                                  <SelectItem value="Trancado">Trancado</SelectItem>
                                  <SelectItem value="Formado">Formado</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="form-group">
                              <Label htmlFor="user-institution" className="form-label">Instituição de Ensino</Label>
                              <Input id="user-institution" value={userForm.institution} onChange={(e) => setUserForm(prev => ({ ...prev, institution: e.target.value }))} data-testid="user-institution-input" placeholder="Nome da instituição" className="dialog-input" />
                            </div>
                            <div className="form-group">
                              <Label htmlFor="user-education-level" className="form-label">Nível de Formação</Label>
                              <Select value={userForm.education_level} onValueChange={(value) => setUserForm(prev => ({ ...prev, education_level: value }))} data-testid="user-education-level-select">
                                <SelectTrigger className="form-input"><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                  <SelectItem value="Ensino Médio">Ensino Médio</SelectItem>
                                  <SelectItem value="Técnico">Técnico</SelectItem>
                                  <SelectItem value="Graduação">Graduação</SelectItem>
                                  <SelectItem value="Pós-Graduação">Pós-Graduação</SelectItem>
                                  <SelectItem value="Mestrado">Mestrado</SelectItem>
                                  <SelectItem value="Doutorado">Doutorado</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="form-group">
                            <Label htmlFor="user-mentor" className="form-label">Responsável Direto</Label>
                            <Input id="user-mentor" value={userForm.mentor} onChange={(e) => setUserForm(prev => ({ ...prev, mentor: e.target.value }))} data-testid="user-mentor-input" placeholder="Nome do responsável" className="dialog-input" />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Lock size={10} style={{ color: '#818cf8' }} />
                            </div>
                            <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Permissões e Acesso</span>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="form-group">
                              <Label htmlFor="user-sector" className="form-label">Setor *</Label>
                              <Select value={userForm.sector} onValueChange={(value) => setUserForm(prev => ({ ...prev, sector: value }))} data-testid="user-sector-select">
                                <SelectTrigger className="form-input"><SelectValue placeholder="Selecione o setor" /></SelectTrigger>
                                <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                  {sectors.map(sector => (
                                    <SelectItem key={sector.name} value={sector.name}>{sector.display_name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {systemUserMode ? (
                              <div className="form-group">
                                <Label htmlFor="user-role" className="form-label">Função *</Label>
                                <Select value={userForm.role !== 'employee' ? userForm.role : 'coordinator'} onValueChange={(value) => setUserForm(prev => ({ ...prev, role: value }))} data-testid="user-role-select">
                                  <SelectTrigger className="form-input"><SelectValue placeholder="Selecione a função" /></SelectTrigger>
                                  <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                    <SelectItem value="coordinator">Coordenador</SelectItem>
                                    <SelectItem value="admin">Administrador</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : (
                              <div className="form-group">
                                <Label className="form-label">Função</Label>
                                <div style={{ display: 'flex', alignItems: 'center', height: '2.2rem', padding: '0 0.65rem', borderRadius: 7, border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)', fontSize: '0.8rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif" }}>
                                  Aluno
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <Button
                            type="button"
                            onClick={() => setUserDialogOpen(false)}
                            data-testid="cancel-user-btn"
                            style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                          >
                            Cancelar
                          </Button>
                          <Button
                            type="submit"
                            data-testid="save-user-btn"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(99,102,241,0.18)', border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                          >
                            {editingUser ? 'Atualizar Usuário' : 'Criar Usuário'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
            </div>

            {/* ── User list table ── */}
            <div style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, overflow: 'hidden' }}>

              {/* Column headers */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.2fr 1fr 1fr 100px',
                gap: '0',
                padding: '0.55rem 1.1rem',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                background: 'rgba(255,255,255,0.02)',
              }}>
                {['Colaborador', 'Setor', 'Progresso', 'Entrada', ''].map((col, i) => (
                  <div key={i} style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.62rem', fontWeight: 700, color: '#3a3a5a', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: i === 4 ? 'right' : 'left' }}>
                    {col}
                  </div>
                ))}
              </div>

              {/* Rows */}
              {users.filter(u => u.role === 'employee').map((userData, idx) => {
                const pu = progressUsers.find(p => p.user_id === userData.id) || { summary: { total_courses: 0, completed: 0, passed_assessments: 0, total_assessments: 0 } };
                const total = pu.summary?.total_courses || 0;
                const done = pu.summary?.completed || 0;
                const perc = total > 0 ? Math.round((done / total) * 100) : 0;
                const totalAssessments = pu.summary?.total_assessments || 0;
                const passedAssessments = pu.summary?.passed_assessments || 0;
                const isApproved = totalAssessments > 0 && passedAssessments >= totalAssessments;

                // Avatar color from name
                const avatarColors = ['#6366f1','#f97316','#22c55e','#a855f7','#38bdf8','#f43f5e','#14b8a6','#f59e0b'];
                const avatarColor = avatarColors[(userData.name || '').charCodeAt(0) % avatarColors.length];
                const initials = (userData.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

                // Sector color
                const sectorPalette = ['#6366f1','#22c55e','#f97316','#a855f7','#38bdf8','#f43f5e','#14b8a6'];
                const sectorColor = sectorPalette[(userData.sector || '').length % sectorPalette.length];

                return (
                  <div
                    key={userData.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1.2fr 1fr 1fr 100px',
                      gap: '0',
                      padding: '0.7rem 1.1rem',
                      borderBottom: idx < users.filter(u => u.role === 'employee').length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                      background: '#12121a',
                      alignItems: 'center',
                      transition: 'background 0.12s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.05)'}
                    onMouseLeave={e => e.currentTarget.style.background = '#12121a'}
                  >
                    {/* Collaborator: avatar + name + email */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', minWidth: 0 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(135deg, ${avatarColor}cc 0%, ${avatarColor}88 100%)`,
                        border: `1px solid ${avatarColor}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.72rem', fontWeight: 700, color: '#fff',
                        fontFamily: "'Space Grotesk', sans-serif",
                        letterSpacing: '0.02em',
                      }}>
                        {initials}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#d8d8e8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {userData.name}
                        </div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {userData.email}
                        </div>
                      </div>
                    </div>

                    {/* Sector */}
                    <div>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '2px 9px', borderRadius: 5,
                        fontSize: '0.68rem', fontWeight: 600,
                        fontFamily: "'Inter', sans-serif",
                        background: `${sectorColor}18`,
                        color: sectorColor,
                        border: `1px solid ${sectorColor}28`,
                        whiteSpace: 'nowrap',
                      }}>
                        {getSectorDisplayName(userData.sector) || userData.sector || '—'}
                      </span>
                    </div>

                    {/* Progress */}
                    <div style={{ paddingRight: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '4px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#7a7a9a' }}>{done}/{total}</span>
                        {totalAssessments > 0 && (
                          <span style={{
                            fontSize: '0.6rem', fontFamily: "'Inter', sans-serif", fontWeight: 600,
                            padding: '1px 6px', borderRadius: 4,
                            background: isApproved ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                            color: isApproved ? '#22c55e' : '#f87171',
                            border: `1px solid ${isApproved ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                          }}>
                            {isApproved ? 'Aprovado' : 'Pendente'}
                          </span>
                        )}
                      </div>
                      <div style={{ width: '100%', height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 999 }}>
                        <div style={{
                          height: 3, borderRadius: 999,
                          width: `${perc}%`,
                          background: perc >= 100 ? '#22c55e' : perc >= 50 ? '#6366f1' : '#f97316',
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                    </div>

                    {/* Entry date */}
                    <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#3a3a5a' }}>
                      {userData.created_at ? new Date(userData.created_at).toLocaleDateString('pt-BR') : '—'}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                      <button
                        data-testid={`view-user-${userData.id}`}
                        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.07)', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4a4a6a', transition: 'all 0.12s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#c8c8d8'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4a4a6a'; }}
                      >
                        <Eye size={13} />
                      </button>
                      <button
                        onClick={() => { setSystemUserMode(false); handleEditUser(userData); }}
                        data-testid={`edit-user-${userData.id}`}
                        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.07)', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4a4a6a', transition: 'all 0.12s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.1)'; e.currentTarget.style.color = '#818cf8'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4a4a6a'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
                      >
                        <Edit size={13} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(userData.id, userData.name)}
                        data-testid={`delete-user-${userData.id}`}
                        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.07)', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4a4a6a', transition: 'all 0.12s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4a4a6a'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Empty state */}
              {users.filter(u => u.role === 'employee').length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 1.5rem', gap: '0.75rem' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <GraduationCap size={20} style={{ color: '#4a4a6a' }} />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.88rem', fontWeight: 600, color: '#4a4a6a', marginBottom: '0.25rem' }}>Nenhum aluno cadastrado</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: '#2a2a4a' }}>Os alunos aparecerão aqui quando se registrarem na plataforma.</div>
                  </div>
                  <button
                    onClick={() => { setSystemUserMode(false); resetUserForm(); setUserDialogOpen(true); }}
                    data-testid="create-first-user-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.45rem 1rem', borderRadius: 8, cursor: 'pointer',
                      background: 'rgba(99,102,241,0.15)',
                      border: '1px solid rgba(99,102,241,0.28)',
                      color: '#818cf8', fontSize: '0.79rem', fontWeight: 600,
                      fontFamily: "'Inter', sans-serif", marginTop: '0.25rem',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Plus size={14} />
                    Cadastrar Primeiro Aluno
                  </button>
                </div>
              )}
            </div>

          </TabsContent>

          <TabsContent value="sectors" className="mt-0">
            {/* Sectors header */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              gap: '1rem', flexWrap: 'wrap', paddingTop: '1.5rem', marginBottom: '1.25rem',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.2rem' }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: 'rgba(20,184,166,0.1)',
                    border: '1px solid rgba(20,184,166,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Building size={14} style={{ color: '#14b8a6' }} />
                  </div>
                  <h2 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '1.08rem', fontWeight: 700,
                    color: '#e8e8f0', margin: 0, letterSpacing: '-0.01em',
                  }}>
                    Setores
                  </h2>
                  {sectors.length > 0 && (
                    <span style={{
                      fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 600,
                      color: '#14b8a6', background: 'rgba(20,184,166,0.1)',
                      border: '1px solid rgba(20,184,166,0.2)',
                      padding: '1px 7px', borderRadius: 5,
                    }}>
                      {sectors.length}
                    </span>
                  )}
                </div>
                <p style={{
                  fontSize: '0.77rem', color: '#4a4a6a',
                  fontFamily: "'Inter', sans-serif", margin: 0, paddingLeft: '2.55rem',
                }}>
                  Organize colaboradores e conteúdo por departamento
                </p>
              </div>

              <Dialog
                open={sectorDialogOpen}
                onOpenChange={(isOpen) => {
                  setSectorDialogOpen(isOpen);
                  if (!isOpen) resetSectorForm();
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    onClick={resetSectorForm}
                    data-testid="create-sector-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                      padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto',
                      background: 'rgba(20,184,166,0.12)',
                      border: '1px solid rgba(20,184,166,0.22)',
                      color: '#2dd4bf',
                      fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                      letterSpacing: '0.01em',
                    }}
                  >
                    <Plus size={14} />
                    Novo Setor
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
                  <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
                    <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
                      <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(20,184,166,0.12)', border: '1px solid rgba(20,184,166,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Building size={13} style={{ color: '#2dd4bf' }} />
                      </div>
                      {editingSector ? 'Editar Setor' : 'Novo Setor'}
                    </DialogTitle>
                    <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
                      {editingSector ? 'Atualize as informações do setor' : 'Adicione um novo setor para organizar os colaboradores'}
                    </DialogDescription>
                  </DialogHeader>

                  <form onSubmit={handleSaveSector} className="space-y-3">
                    <div className="form-group">
                      <Label htmlFor="sector-name" className="form-label">Identificador *</Label>
                      <Input
                        id="sector-name"
                        value={sectorForm.name}
                        onChange={(e) => !editingSector && setSectorForm(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                        required
                        disabled={!!editingSector}
                        data-testid="sector-name-input"
                        placeholder="Ex: marketing (minúsculo_sem_espaços)"
                        className="dialog-input"
                        style={editingSector ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      />
                      <p style={{ fontSize: '0.68rem', color: '#3a3a5a', marginTop: '0.25rem', fontFamily: "'Inter', sans-serif" }}>
                        {editingSector ? 'O identificador não pode ser alterado.' : 'Convertido para minúsculo_sem_espaços automaticamente'}
                      </p>
                    </div>

                    <div className="form-group">
                      <Label htmlFor="sector-display-name" className="form-label">Nome de Exibição *</Label>
                      <Input
                        id="sector-display-name"
                        value={sectorForm.display_name}
                        onChange={(e) => setSectorForm(prev => ({ ...prev, display_name: e.target.value }))}
                        required
                        data-testid="sector-display-name-input"
                        placeholder="Ex: Marketing, Vendas, Recursos Humanos"
                        className="dialog-input"
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="sector-description" className="form-label">Descrição</Label>
                      <Textarea
                        id="sector-description"
                        value={sectorForm.description}
                        onChange={(e) => setSectorForm(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        data-testid="sector-description-input"
                        placeholder="Descrição do setor (opcional)"
                        className="dialog-textarea"
                      />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <Button
                        type="button"
                        onClick={() => setSectorDialogOpen(false)}
                        data-testid="cancel-sector-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        data-testid="save-sector-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(20,184,166,0.15)', border: '1px solid rgba(20,184,166,0.28)', color: '#2dd4bf', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                      >
                        {editingSector ? 'Atualizar Setor' : 'Criar Setor'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Sectors list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {sectors.map(sector => {
                const userCount = users.filter(u => u.sector === sector.name).length;
                return (
                  <div key={sector.id} style={{
                    background: '#12121a',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: 11,
                    padding: '0.7rem 0.875rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                  }}>
                    {/* Icon badge */}
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0, marginTop: '0.05rem',
                      background: 'rgba(20,184,166,0.08)',
                      border: '1px solid rgba(20,184,166,0.13)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Building size={14} style={{ color: '#14b8a6' }} />
                    </div>

                    {/* Body */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title + identifier + status */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                        <span style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: '0.88rem', fontWeight: 700, color: '#e0e0f0',
                        }}>
                          {sector.display_name}
                        </span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '1px 6px', borderRadius: 4,
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          fontSize: '0.65rem', color: '#4a4a6a',
                          fontFamily: 'monospace', letterSpacing: '0.02em',
                        }}>
                          {sector.name}
                        </span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '1px 6px', borderRadius: 4,
                          background: sector.is_active ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${sector.is_active ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.07)'}`,
                          fontSize: '0.65rem', fontWeight: 600,
                          color: sector.is_active ? '#4ade80' : '#4a4a6a',
                          fontFamily: "'Inter', sans-serif",
                        }}>
                          {sector.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>

                      {/* Description */}
                      {sector.description && (
                        <p style={{
                          fontSize: '0.75rem', color: '#4a4a6a',
                          fontFamily: "'Inter', sans-serif", margin: '0 0 0.4rem',
                          lineHeight: 1.5,
                          overflow: 'hidden', display: '-webkit-box',
                          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                          {sector.description}
                        </p>
                      )}

                      {/* Meta row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <span style={{
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                          fontSize: '0.68rem', color: '#4a4a6a',
                          fontFamily: "'Inter', sans-serif",
                        }}>
                          <Users size={10} style={{ color: '#3a3a5a' }} />
                          {userCount} colaborador{userCount !== 1 ? 'es' : ''}
                        </span>
                        <span style={{
                          fontSize: '0.65rem', color: '#3a3a5a',
                          fontFamily: 'monospace',
                        }}>
                          Criado em {new Date(sector.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                      <button data-testid={`view-sector-${sector.id}`} title="Visualizar" className="admin-icon-btn">
                        <Eye size={12} />
                      </button>
                      <button onClick={() => handleEditSector(sector)} data-testid={`edit-sector-${sector.id}`} title="Editar" className="admin-icon-btn edit">
                        <Edit size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {sectors.length === 0 && (
                <div style={{
                  background: '#12121a',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 12, padding: '2.5rem 1.5rem', textAlign: 'center',
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 11,
                    background: 'rgba(20,184,166,0.07)',
                    border: '1px solid rgba(20,184,166,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 0.85rem',
                  }}>
                    <Building size={20} style={{ color: '#0f5a52' }} />
                  </div>
                  <h3 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '0.92rem', fontWeight: 600, color: '#6a6a8a', marginBottom: '0.3rem',
                  }}>
                    Nenhum setor encontrado
                  </h3>
                  <p style={{
                    fontSize: '0.77rem', color: '#3a3a5a', marginBottom: '1.1rem',
                    fontFamily: "'Inter', sans-serif",
                  }}>
                    Crie setores para organizar melhor seus colaboradores.
                  </p>
                  <button
                    onClick={() => setSectorDialogOpen(true)}
                    data-testid="create-first-sector-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.45rem 1rem', borderRadius: 8,
                      background: 'rgba(20,184,166,0.12)',
                      border: '1px solid rgba(20,184,166,0.22)',
                      color: '#2dd4bf',
                      fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    <Plus size={13} />
                    Criar Primeiro Setor
                  </button>
                </div>
              )}
            </div>

          </TabsContent>

          <TabsContent value="settings" className="space-y-8 mt-6">

            {/* ── Usuários do Sistema ── */}
            <div className="admin-section">
              <div className="admin-section-header">
                <div className="admin-section-header-left">
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Shield size={14} style={{ color: '#818cf8' }} />
                  </div>
                  <div>
                    <p className="admin-section-title">Usuários do Sistema</p>
                    <p className="admin-section-description">Administradores e coordenadores com acesso ao painel</p>
                  </div>
                </div>
                  <Dialog
                    open={userDialogOpen && systemUserMode}
                    onOpenChange={(isOpen) => {
                      if (!isOpen) { setSystemUserMode(false); resetUserForm(); }
                      setUserDialogOpen(isOpen);
                    }}
                  >
                    <DialogTrigger asChild>
                      <button
                        onClick={() => {
                          setSystemUserMode(true);
                          resetUserForm();
                          setUserForm(prev => ({ ...prev, role: 'coordinator' }));
                          setUserDialogOpen(true);
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, cursor: 'pointer', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.28)', color: '#818cf8', fontSize: '0.79rem', fontWeight: 600, fontFamily: "'Inter', sans-serif", letterSpacing: '0.01em', transition: 'all 0.15s ease' }}
                      >
                        <Plus size={14} />
                        Novo Usuário do Sistema
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
                      <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
                        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
                          <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users size={13} style={{ color: '#818cf8' }} />
                          </div>
                          {editingUser ? 'Editar Usuário do Sistema' : 'Novo Usuário do Sistema'}
                        </DialogTitle>
                        <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
                          {editingUser ? 'Atualize os dados do usuário do sistema' : 'Cadastre um administrador ou coordenador'}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSaveUser} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="form-group">
                            <Label className="form-label">Nome Completo *</Label>
                            <Input value={userForm.name} onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))} required placeholder="Nome do usuário" className="dialog-input" />
                          </div>
                          <div className="form-group">
                            <Label className="form-label">Email *</Label>
                            <Input type="email" value={userForm.email} onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))} required placeholder="email@empresa.com" className="dialog-input" />
                          </div>
                        </div>
                        <div className="form-group">
                          <Label className="form-label">{editingUser ? 'Nova Senha (deixe em branco para não alterar)' : 'Senha *'}</Label>
                          <Input type="password" value={userForm.password} onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))} required={!editingUser} placeholder={editingUser ? 'Digite a nova senha' : 'Senha inicial'} className="dialog-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="form-group">
                            <Label className="form-label">Setor *</Label>
                            <Select value={userForm.sector} onValueChange={(value) => setUserForm(prev => ({ ...prev, sector: value }))}>
                              <SelectTrigger className="form-input"><SelectValue placeholder="Selecione o setor" /></SelectTrigger>
                              <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                {sectors.map(sector => (
                                  <SelectItem key={sector.name} value={sector.name}>{sector.display_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="form-group">
                            <Label className="form-label">Função *</Label>
                            <Select value={userForm.role !== 'employee' ? userForm.role : 'coordinator'} onValueChange={(value) => setUserForm(prev => ({ ...prev, role: value }))}>
                              <SelectTrigger className="form-input"><SelectValue placeholder="Selecione a função" /></SelectTrigger>
                              <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                <SelectItem value="coordinator">Coordenador</SelectItem>
                                <SelectItem value="admin">Administrador</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <Button type="button" onClick={() => { setSystemUserMode(false); setUserDialogOpen(false); resetUserForm(); }} style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                            Cancelar
                          </Button>
                          <Button type="submit" style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(99,102,241,0.18)', border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                            {editingUser ? 'Atualizar' : 'Criar'} Usuário
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
              </div>
              <div className="admin-section-body" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {users.filter(u => u.role === 'admin' || u.role === 'coordinator').map(sysUser => {
                    const roleColor = sysUser.role === 'admin' ? '#ef4444' : '#6366f1';
                    return (
                    <div key={sysUser.id} className="admin-list-row">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${roleColor}cc 0%, ${roleColor}88 100%)`, border: `1px solid ${roleColor}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, color: '#fff', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.02em' }}>
                          {sysUser.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#d8d8e8', margin: 0 }}>{sysUser.name}</p>
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#3a3a5a', margin: 0 }}>{sysUser.email}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="admin-badge" style={{ background: `${roleColor}18`, color: sysUser.role === 'admin' ? '#f87171' : '#818cf8', border: `1px solid ${roleColor}28` }}>
                          {sysUser.role === 'admin' ? 'Administrador' : 'Coordenador'}
                        </span>
                        <button onClick={() => { setSystemUserMode(true); handleEditUser(sysUser); }} title="Editar" className="admin-icon-btn edit">
                          <Edit size={12} />
                        </button>
                        <button onClick={() => handleDeleteUser(sysUser.id, sysUser.name)} title="Excluir" className="admin-icon-btn danger delete">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                  {users.filter(u => u.role === 'admin' || u.role === 'coordinator').length === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2.5rem 1.5rem', gap: '0.6rem' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Shield size={18} style={{ color: '#3a3a6a' }} />
                      </div>
                      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.85rem', fontWeight: 600, color: '#4a4a6a', margin: 0 }}>Nenhum usuário do sistema cadastrado</p>
                    </div>
                  )}
              </div>
            </div>

            {/* ── Meu Perfil ── */}
            <div className="admin-section">
              <div className="admin-section-header">
                <div className="admin-section-header-left">
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <User size={14} style={{ color: '#60a5fa' }} />
                  </div>
                  <div>
                    <p className="admin-section-title">Meu Perfil</p>
                    <p className="admin-section-description">Dados da sua conta e informações pessoais</p>
                  </div>
                </div>
              </div>
              <div className="admin-section-body">
                {/* Dados de conta (somente leitura) */}
                {user && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', padding: '0.75rem', borderRadius: 9, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Shield size={13} style={{ color: '#4a4a6a', flexShrink: 0 }} />
                      <div>
                        <p style={{ fontSize: '0.65rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif", margin: '0 0 0.2rem' }}>Perfil de acesso</p>
                        <span className="admin-badge" style={{
                          background: user.role === 'admin' ? 'rgba(239,68,68,0.1)' : 'rgba(99,102,241,0.1)',
                          color: user.role === 'admin' ? '#f87171' : user.role === 'coordinator' ? '#818cf8' : '#4ade80',
                          border: `1px solid ${user.role === 'admin' ? 'rgba(239,68,68,0.2)' : 'rgba(99,102,241,0.2)'}`,
                        }}>
                          {user.role === 'admin' ? 'Administrador' : user.role === 'coordinator' ? 'Coordenador' : 'Aluno'}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Briefcase size={13} style={{ color: '#4a4a6a', flexShrink: 0 }} />
                      <div>
                        <p style={{ fontSize: '0.65rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif", margin: '0 0 0.15rem' }}>Setor</p>
                        <p style={{ fontSize: '0.8rem', fontWeight: 500, color: '#c0c0d8', fontFamily: "'Inter', sans-serif", margin: 0 }}>
                          {user.sector === 'estagio' ? 'Estágio' : user.sector === 'tecnico' ? 'Técnico' : user.sector === 'comercial' ? 'Comercial' : user.sector || '—'}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: user.is_active ? '#22c55e' : '#f87171' }} />
                      <div>
                        <p style={{ fontSize: '0.65rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif", margin: '0 0 0.15rem' }}>Status</p>
                        <p style={{ fontSize: '0.8rem', fontWeight: 500, color: '#c0c0d8', fontFamily: "'Inter', sans-serif", margin: 0 }}>{user.is_active ? 'Conta ativa' : 'Conta inativa'}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleUpdateAdminProfile} className="space-y-4">
                  {/* Sub-section: Informações Básicas */}
                  <div className="admin-sub-section">
                    <div style={{ width: 18, height: 18, borderRadius: 4, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <User size={9} style={{ color: '#60a5fa' }} />
                    </div>
                    <span className="admin-sub-section-label">Informações Básicas</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="form-group">
                      <Label className="form-label">Nome Completo</Label>
                      <Input value={adminProfileForm.name} onChange={e => setAdminProfileForm(p => ({ ...p, name: e.target.value }))} placeholder="Seu nome completo" className="dialog-input" />
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Email</Label>
                      <Input type="email" value={adminProfileForm.email} onChange={e => setAdminProfileForm(p => ({ ...p, email: e.target.value }))} placeholder="seu@email.com" className="dialog-input" />
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Telefone</Label>
                      <Input value={adminProfileForm.phone} onChange={e => setAdminProfileForm(p => ({ ...p, phone: e.target.value }))} placeholder="(00) 00000-0000" className="dialog-input" />
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Matrícula</Label>
                      <Input value={adminProfileForm.code} onChange={e => setAdminProfileForm(p => ({ ...p, code: e.target.value }))} placeholder="Número de matrícula" className="dialog-input" />
                    </div>
                  </div>
                  <div className="form-group">
                    <Label className="form-label">Endereço</Label>
                    <Input value={adminProfileForm.address} onChange={e => setAdminProfileForm(p => ({ ...p, address: e.target.value }))} placeholder="Rua, número, bairro, cidade" className="dialog-input" />
                  </div>

                  {/* Sub-section: Informações Acadêmicas */}
                  <div className="admin-sub-section" style={{ marginTop: '0.5rem' }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <GraduationCap size={9} style={{ color: '#a855f7' }} />
                    </div>
                    <span className="admin-sub-section-label">Informações Acadêmicas</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="form-group">
                      <Label className="form-label">Turno</Label>
                      <Select value={adminProfileForm.shift} onValueChange={v => setAdminProfileForm(p => ({ ...p, shift: v }))}>
                        <SelectTrigger className="form-input"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                          <SelectItem value="Matutino">Matutino</SelectItem>
                          <SelectItem value="Vespertino">Vespertino</SelectItem>
                          <SelectItem value="Noturno">Noturno</SelectItem>
                          <SelectItem value="Integral">Integral</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Período</Label>
                      <Input value={adminProfileForm.period} onChange={e => setAdminProfileForm(p => ({ ...p, period: e.target.value }))} placeholder="Ex: 3º Semestre" className="dialog-input" />
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Modalidade</Label>
                      <Select value={adminProfileForm.modality} onValueChange={v => setAdminProfileForm(p => ({ ...p, modality: v }))}>
                        <SelectTrigger className="form-input"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                          <SelectItem value="Presencial">Presencial</SelectItem>
                          <SelectItem value="EAD">EAD</SelectItem>
                          <SelectItem value="Híbrido">Híbrido</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Data de Ingresso</Label>
                      <Input value={adminProfileForm.start_date} onChange={e => setAdminProfileForm(p => ({ ...p, start_date: e.target.value }))} placeholder="DD/MM/AAAA" className="dialog-input" />
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Nível de Formação</Label>
                      <Select value={adminProfileForm.education_level} onValueChange={v => setAdminProfileForm(p => ({ ...p, education_level: v }))}>
                        <SelectTrigger className="form-input"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                          <SelectItem value="Ensino Médio">Ensino Médio</SelectItem>
                          <SelectItem value="Técnico">Técnico</SelectItem>
                          <SelectItem value="Graduação">Graduação</SelectItem>
                          <SelectItem value="Pós-Graduação">Pós-Graduação</SelectItem>
                          <SelectItem value="Mestrado">Mestrado</SelectItem>
                          <SelectItem value="Doutorado">Doutorado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-group">
                      <Label className="form-label">Instituição de Ensino</Label>
                      <Input value={adminProfileForm.institution} onChange={e => setAdminProfileForm(p => ({ ...p, institution: e.target.value }))} placeholder="Nome da instituição" className="dialog-input" />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <Button type="submit" disabled={adminProfileLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.28)', color: '#60a5fa', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                      <Save size={13} />
                      {adminProfileLoading ? "Salvando..." : "Salvar Perfil"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>

            {/* ── Configurações da Plataforma ── */}
            <div className="admin-section">
              <div className="admin-section-header">
                <div className="admin-section-header-left">
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Settings size={14} style={{ color: '#a855f7' }} />
                  </div>
                  <div>
                    <p className="admin-section-title">Configurações da Plataforma</p>
                    <p className="admin-section-description">Ajustes gerais exibidos para todos os alunos</p>
                  </div>
                </div>
              </div>
              <div className="admin-section-body">
                <form onSubmit={handleUpdateSettings} className="space-y-4">

                  {/* Video welcome toggle */}
                  <div className="admin-toggle-row">
                    <div style={{ width: 30, height: 30, borderRadius: 7, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Video size={13} style={{ color: '#a855f7' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#c0c0d8', margin: 0 }}>Exibir vídeo de boas-vindas</p>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.72rem', color: '#4a4a6a', margin: 0 }}>Mostrar vídeo automático ao aluno no primeiro acesso</p>
                    </div>
                    <label className="admin-switch">
                      <input
                        type="checkbox"
                        checked={settingsForm.show_welcome_video}
                        onChange={e => setSettingsForm(p => ({ ...p, show_welcome_video: e.target.checked }))}
                      />
                      <div className="admin-switch-track" />
                    </label>
                  </div>

                  {/* Sub-section: Vídeo */}
                  <div className="admin-sub-section" style={{ marginTop: '0.25rem' }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Video size={9} style={{ color: '#a855f7' }} />
                    </div>
                    <span className="admin-sub-section-label">Vídeo Institucional</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="form-group">
                      <Label className="form-label">Título do Vídeo</Label>
                      <Input value={settingsForm.welcome_video_title} onChange={e => setSettingsForm(p => ({ ...p, welcome_video_title: e.target.value }))} placeholder="Bem-vindo à Escola de Suporti" className="dialog-input" />
                    </div>
                    <div className="form-group">
                      <Label className="form-label">URL do Vídeo</Label>
                      <Input value={settingsForm.welcome_video_url} onChange={e => setSettingsForm(p => ({ ...p, welcome_video_url: e.target.value }))} placeholder="https://..." className="dialog-input" />
                    </div>
                  </div>
                  <div className="form-group">
                    <Label className="form-label">Descrição do Vídeo</Label>
                    <Textarea value={settingsForm.welcome_video_description} onChange={e => setSettingsForm(p => ({ ...p, welcome_video_description: e.target.value }))} placeholder="Descrição exibida junto ao vídeo" rows={3} className="dialog-textarea" />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <Button type="submit" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.28)', color: '#c084fc', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                      <Save size={13} />
                      Salvar Configurações
                    </Button>
                  </div>
                </form>
              </div>
            </div>

          </TabsContent>

        </Tabs>
        <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
                <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
                  <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
                    <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FileText size={13} style={{ color: '#60a5fa' }} />
                    </div>
                    Relatório Detalhado do Aluno
                  </DialogTitle>
                  {reportData && <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>{reportData.name} — {reportData.email}</DialogDescription>}
                </DialogHeader>
                {reportLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#60a5fa' }} />
                </div>
                ) : reportData ? (
                <div className="space-y-6">
                    {/* Progresso dos Cursos */}
                    <div>
                    <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Space Grotesk', sans-serif", marginBottom: '0.6rem' }}>Progresso nos Cursos</h3>
                    <div className="space-y-3">
                        {reportData.course_progress.length > 0 ? reportData.course_progress.map(progress => (
                        <div key={progress.course_id} className="p-3 rounded-lg" style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
                            <div className="flex justify-between items-center mb-2">
                            <p style={{ fontWeight: 600, fontSize: '0.82rem', color: '#d0d0e8' }}>{progress.course_title}</p>
                            <Badge variant={progress.is_completed ? "success" : "secondary"}>
                                {progress.is_completed ? "Concluído" : "Em Andamento"}
                            </Badge>
                            </div>
                            <div className="w-full rounded-full h-2" style={{ background: 'rgba(255,255,255,0.08)' }}>
                            <div className="h-2 rounded-full" style={{ width: `${progress.completion_percentage}%`, background: 'rgba(59,130,246,0.7)' }}></div>
                            </div>
                            <p className="text-right text-sm mt-1" style={{ color: '#6a6a9a', fontSize: '0.73rem' }}>{progress.completion_percentage.toFixed(0)}%</p>
                        </div>
                        )) : <p style={{ fontSize: '0.8rem', color: '#4a4a6a' }}>Nenhum curso iniciado.</p>}
                    </div>
                    </div>

                    {/* Resultados das Avaliações */}
                    <div>
                    <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Space Grotesk', sans-serif", marginBottom: '0.6rem' }}>Resultados das Avaliações</h3>
                    <div className="space-y-3">
                        {reportData.assessment_results.length > 0 ? reportData.assessment_results.map(result => (
                        <div key={result.assessment_id} className="flex justify-between items-center p-3 rounded-lg" style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
                            <div>
                            <p style={{ fontWeight: 600, fontSize: '0.82rem', color: '#d0d0e8' }}>{result.assessment_title}</p>
                            <p style={{ fontSize: '0.73rem', color: '#4a4a6a', marginTop: '0.1rem' }}>
                                Concluído em: {new Date(result.completed_at).toLocaleDateString('pt-BR')}
                            </p>
                            </div>
                            <div className="text-right">
                            <p style={{ fontWeight: 700, fontSize: '0.9rem', color: result.passed ? '#4ade80' : '#f87171' }}>
                                {result.percentage.toFixed(1)}% — {result.passed ? "Aprovado" : "Reprovado"}
                            </p>
                            </div>
                        </div>
                        )) : <p style={{ fontSize: '0.8rem', color: '#4a4a6a' }}>Nenhuma avaliação realizada.</p>}
                    </div>
                    </div>
                </div>
                ) : null}
            </DialogContent>
            </Dialog>

        <Dialog open={courseArtifactsJobModalOpen} onOpenChange={handleCourseJobModalChange}>
          <DialogContent className="max-w-lg" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
            <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
              <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Layers size={13} style={{ color: '#818cf8' }} />
                </div>
                Progresso da geração de artefatos
              </DialogTitle>
              <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
                O processamento continua em segundo plano mesmo se você fechar esta tela.
              </DialogDescription>
            </DialogHeader>
            {courseJobInfo ? (
              <div className="space-y-4">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.78rem', color: '#8a8ab0' }}>
                    <span>Status geral</span>
                    <span>{courseJobInfo.segments_processed}/{courseJobInfo.total_segments} vídeos</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={getCourseJobBadgeClass()}>
                      {courseJobStatus || '—'}
                    </Badge>
                    <span style={{ fontSize: '0.73rem', color: '#4a4a6a' }}>
                      {Math.round(courseJobInfo.percent_complete || 0)}%
                    </span>
                  </div>
                  <Progress value={courseJobInfo.percent_complete || 0} className="h-2 mt-2" />
                </div>
                <div className="p-3 rounded-lg space-y-2" style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: 600, color: '#c0c0d8' }}>
                    <span>Vídeo atual</span>
                    <span style={{ color: '#8a8ab0' }}>{courseJobInfo.current_segment_title || '—'}</span>
                  </div>
                  <p style={{ fontSize: '0.73rem', color: '#4a4a6a' }}>{getSegmentStageLabel()}</p>
                  {currentSegmentProgress?.percent !== undefined && (
                    <Progress value={currentSegmentProgress.percent || 0} className="h-2" />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.73rem', color: '#4a4a6a' }}>
                    <span>Artefato atual</span>
                    <span style={{ fontWeight: 600, color: '#8a8ab0' }}>
                      {courseJobInfo.current_artifact ? (artifactLabelMap[courseJobInfo.current_artifact] || courseJobInfo.current_artifact) : '—'}
                    </span>
                  </div>
                </div>
                {courseArtifactsJobData?.current_segment_error && (
                  <div className="p-2 rounded" style={{ border: '1px solid rgba(248,113,113,0.25)', background: 'rgba(248,113,113,0.07)', fontSize: '0.78rem', color: '#f87171' }}>
                    {courseArtifactsJobData.current_segment_error}
                  </div>
                )}
                {courseJobInfo?.error_message && (
                  <div className="p-2 rounded" style={{ border: '1px solid rgba(248,113,113,0.25)', background: 'rgba(248,113,113,0.07)', fontSize: '0.78rem', color: '#f87171' }}>
                    {courseJobInfo.error_message}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-center items-center h-32">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#818cf8' }} />
              </div>
            )}
          </DialogContent>
        </Dialog>

            {/* Video Upload Manager Modal */}
            {showVideoUploadManager && (
              <VideoUploadManager
                onVideoUploaded={(videoId, videoData) => {
                  setNewSegment(prev => ({
                    ...prev,
                    video_id: videoId,
                    video_url: null,
                    video_type: "local",
                    title: videoData.title
                  }));
                  setShowVideoUploadManager(false);
                  toast.success("Vídeo e transcrição prontos!");
                }}
                onCancel={() => setShowVideoUploadManager(false)}
              />
            )}

      {/* Confirm Delete Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="max-w-sm w-full mx-4 rounded-xl p-6" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <h3 style={{ fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0', fontFamily: "'Space Grotesk', sans-serif", marginBottom: '0.4rem' }}>{confirmDialog.title}</h3>
            <p style={{ fontSize: '0.8rem', color: '#6a6a9a', fontFamily: "'Inter', sans-serif", marginBottom: '1.5rem' }}>{confirmDialog.message}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={closeConfirmDialog}
                style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
              >
                Cancelar
              </button>
              <button
                onClick={() => { confirmDialog.onConfirm?.(); closeConfirmDialog(); }}
                style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
      </div> {/* fim dashboard-content */}
    </div>   /* fim dashboard-layout */
  );
};

export default AdminDashboard;
